import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { css, keyframes, createGlobalStyle } from 'styled-components';
import axios from 'axios';
import {
  Warehouse,
  Package as PackageIcon,
  Glasses,
  Inbox,
  Send,
  Clock,
  History,
  RefreshCw,
  LogOut,
  CheckCircle2,
  AlertCircle,
  ShoppingCart,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  Download,
  ChevronRight,
  Layers,
  X,
  PackageOpen,
  Printer,
  ListChecks,
  HelpingHand,
  ShoppingBag,
  Boxes,
  LayoutGrid,
  Truck,
} from 'lucide-react';
import LensStockPage from '../LensStock/LensStockPage';
import ThirdPartyHandoffModal from '../../components/ThirdPartyHandoffModal/ThirdPartyHandoffModal';
import FrameStockPage from '../FrameStock/FrameStockPage';
import SupplyStockPage from '../SupplyStock/SupplyStockPage';
import PackageScanner from '../../components/PackageScanner';
import SupplierReceipt from '../../components/SupplierReceipt';
import { useBranding, DEFAULT_BRANDING } from '../../contexts/BrandingContext';
import { buildPackageLabelHtml, openPrintIframe } from '../../utils/packageLabel';
import { useTranslation } from 'react-i18next';
import { canAccess } from '../../utils/tasks';
import {
  LENS_SPH_VALUES,
  LENS_CYL_VALUES,
  LENS_CELL_W as CELL_W,
  LENS_CELL_H as CELL_H,
  LENS_SPH_COL_W as SPH_COL_W,
  formatDecimalKey as lensKey,
  formatCylLabel,
  zoneBg,
} from '../../utils/lensGrid';

// `Send` plays the role of "outgoing" — outgoing orders are dispatched
// from the central warehouse on their way back to the retail store.
const SafeOutbox = Send;

// ─── Design tokens ──────────────────────────────────────────────────
// New language: dark sidebar + light workspace, sharp borders, monochrome
// dominance with one accent. Inspired by Linear / Vercel / Stripe — confident,
// quiet, dense. Everything monospaced where data is data; display where text
// is voice.
const DesignTokens = createGlobalStyle`
  :root {
    /* Motion */
    --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
    --ease-in-out: cubic-bezier(0.77, 0, 0.175, 1);
    --dur-press: 120ms;
    --dur-hover: 160ms;
    --dur-panel: 240ms;
    --dur-modal: 260ms;

    /* Surfaces */
    --canvas: #f7f7f9;
    --surface: #ffffff;
    --surface-2: #fafafb;
    --sidebar: #0c0c0f;
    --sidebar-hover: #18181c;
    --sidebar-active: #1f1f24;

    /* Lines + shadows */
    --line: #e7e7eb;
    --line-soft: #ededf0;
    --shadow-card: 0 0 0 1px var(--line);
    --shadow-pop: 0 24px 60px -12px rgba(12, 12, 15, 0.25);
    --shadow-focus: 0 0 0 3px rgba(91, 141, 239, 0.20);

    /* Ink */
    --ink: #0c0c0f;
    --ink-2: #3a3a44;
    --ink-soft: #5a5a66;
    --ink-mute: #9da3ae;
    --ink-on-dark: #ededf0;
    --ink-on-dark-soft: #8a8a94;

    /* Accent */
    --accent: #5b8def;
    --accent-hover: #4574d6;
    --accent-soft: #eef2ff;
    --accent-ink: #1d4ed8;

    /* Semantic */
    --ok: #16a34a;
    --ok-soft: #ecfdf5;
    --warn: #d97706;
    --warn-soft: #fef3c7;
    --danger: #dc2626;
    --danger-soft: #fee2e2;
  }

  body { background: var(--canvas); }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

const enterUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// ─── Layout shell: fixed dark sidebar + scrollable workspace ────────────
const Page = styled.div`
  min-height: 100vh;
  background: var(--canvas);
  display: grid;
  /* 72px collapsed: 56px of usable rail after padding, which fits a 44px hit
     target with room either side. 64px was too tight — the brand row's dot and
     the collapse button together overflowed it and the button got clipped, so
     once collapsed there was no way back. */
  grid-template-columns: ${(p) => (p.$collapsed ? '72px' : '248px')} minmax(0, 1fr);
  transition: grid-template-columns var(--dur-hover) var(--ease-out);

  /* Mobile: stack sidebar above content. Collapsing is meaningless there — the
     rail is already a horizontal strip. */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Sidebar = styled.aside`
  background: var(--sidebar);
  color: var(--ink-on-dark);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: sticky;
  top: 0;
  padding: ${(p) => (p.$collapsed ? '1.25rem 0.5rem' : '1.25rem 0.75rem')};
  gap: 0.5rem;
  overflow-x: hidden;

  /* Collapsed: icons only. Targeted by class, not by interpolating the styled
     components — NavGroup and NavGroupLabel are declared further down this file
     and referencing them here would be a use-before-define at module load. */
  ${(p) => p.$collapsed && css`
    @media (min-width: 901px) {
      .nav-group-label { display: none; }
      .nav-label { display: none; }
      /* A hairline plus a little air. The expanded 1.1rem gap left the rail
         looking like four stranded clusters once the headings were gone. */
      .nav-group + .nav-group {
        margin-top: 0.4rem;
        padding-top: 0.4rem;
        border-top: 1px solid var(--sidebar-hover);
      }
    }
  `}

  @media (max-width: 900px) {
    position: relative;
    min-height: auto;
    flex-direction: row;
    padding: 0.75rem;
    overflow-x: auto;
    align-items: center;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const BrandMark = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem 1rem;
  border-bottom: 1px solid #1f1f24;
  margin-bottom: 0.25rem;

  /* Collapsed: stack the mark and the toggle instead of sitting them side by
     side. Two 28px controls plus padding cannot fit the rail's width, and the
     one that got clipped was the only way to expand it again. */
  ${(p) => p.$collapsed && css`
    @media (min-width: 901px) {
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      padding: 0.25rem 0 0.75rem;
    }
  `}

  .dot {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }
  @media (max-width: 900px) {
    padding: 0;
    border: none;
    margin: 0;
    flex-shrink: 0;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  /* No entrance animation: this rail is on screen all day, every day. */

  @media (max-width: 900px) {
    flex-direction: row;
    gap: 0.25rem;
  }
`;

const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;

  & + & { margin-top: 1.1rem; }

  /* On a tablet in landscape the rail collapses to a horizontal scroller.
     Group headings would break that row, so they step aside and a thin divider
     carries the grouping instead. */
  @media (max-width: 900px) {
    flex-direction: row;
    gap: 0.25rem;
    & + & {
      margin-top: 0;
      margin-left: 0.5rem;
      padding-left: 0.5rem;
      border-left: 1px solid var(--sidebar-hover);
    }
  }
`;

const NavGroupLabel = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-family: inherit;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-on-dark-soft);
  opacity: 0.65;
  padding: 0.25rem 0.75rem 0.35rem;
  cursor: pointer;
  border-radius: 6px;
  transition: opacity var(--dur-hover) var(--ease-out), background-color var(--dur-hover) var(--ease-out);

  &:hover { opacity: 1; background: var(--sidebar-hover); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent); opacity: 1; }

  .chev {
    margin-left: auto;
    transform: rotate(${(p) => (p.$open ? '0deg' : '-90deg')});
    transition: transform var(--dur-hover) var(--ease-out);
  }

  /* A count of what is hidden, so folding a group never hides a queue. */
  .folded-count {
    font-size: 0.6rem;
    font-weight: 700;
    color: #fff;
    background: var(--accent);
    border-radius: 999px;
    padding: 0 5px;
    min-width: 15px;
    text-align: center;
  }

  @media (prefers-reduced-motion: reduce) {
    .chev { transition: none; }
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavItem = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.55rem 0.75rem;
  border: none;
  background: ${(p) => (p.active ? 'var(--sidebar-active)' : 'transparent')};
  color: ${(p) => (p.active ? '#fff' : 'var(--ink-on-dark-soft)')};
  font-size: 0.85rem;
  font-weight: ${(p) => (p.active ? 600 : 500)};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition:
    background-color var(--dur-hover) var(--ease-out),
    color var(--dur-hover) var(--ease-out),
    transform var(--dur-press) var(--ease-out);

  /* Full-height accent rail. The old one was 2px and inset to half the row's
     height, which at a glance was indistinguishable from the row's own edge —
     the active state was carried almost entirely by a background barely lighter
     than the sidebar. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent);
    border-radius: 0 3px 3px 0;
    opacity: ${(p) => (p.active ? 1 : 0)};
    transition: opacity var(--dur-hover) var(--ease-out);
  }

  /* The icon takes the accent too, so the active row is marked by position,
     fill, colour and weight — it survives greyscale and colour blindness. */
  > svg { color: ${(p) => (p.active ? 'var(--accent)' : 'inherit')}; flex-shrink: 0; }

  &:active { transform: scale(0.98); }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--accent);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${(p) => (p.active ? 'var(--sidebar-active)' : 'var(--sidebar-hover)')};
      color: #fff;
    }
  }

  .badge {
    margin-left: auto;
    display: inline-flex;
    min-width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border-radius: 999px;
    /* Counting badge: quiet, it is only telling you how many things exist. */
    background: transparent;
    color: var(--ink-on-dark-soft);
    border: 1px solid var(--sidebar-hover);
    font-size: 0.68rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  /* Work badge: a queue someone has to empty. Solid accent, white text — the
     one thing in the rail that is allowed to shout. */
  &[data-work="true"] .badge {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }

  /* Collapsed rail: the icon centres and the count rides its top-right corner,
     so a queue is still visible without the label to sit beside. */
  &[data-collapsed="true"] {
    justify-content: center;
    padding: 0.6rem 0;

    .badge {
      position: absolute;
      /* Clear of the glyph rather than sitting on it — a filled pill straddling
         the icon made both unreadable. */
      top: 1px;
      right: 6px;
      margin-left: 0;
      min-width: 15px;
      height: 15px;
      padding: 0 3px;
      font-size: 0.56rem;
      /* Ring in the rail's own colour so the pill reads as floating above the
         icon instead of merging into it. */
      box-shadow: 0 0 0 2px var(--sidebar);
    }

    /* Transparent + hairline is invisible on top of an icon, so counting
       badges take a solid quiet fill only in this mode. */
    &:not([data-work="true"]) .badge {
      background: var(--sidebar-hover);
      border-color: transparent;
      color: var(--ink-on-dark);
    }
  }
`;

const NavCollapseButton = styled.button`
  /* auto only while there is a label to push against; centred once stacked. */
  margin-left: ${(p) => (p.$collapsed ? '0' : 'auto')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: 1px solid var(--sidebar-hover);
  border-radius: 8px;
  background: transparent;
  color: var(--ink-on-dark-soft);
  cursor: pointer;
  transition: background-color var(--dur-hover) var(--ease-out), color var(--dur-hover) var(--ease-out);

  &:hover { background: var(--sidebar-hover); color: #fff; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent); }

  /* Collapsing is meaningless once the rail is a horizontal strip. */
  @media (max-width: 900px) { display: none; }
`;

const NavSpacer = styled.div` flex: 1; @media (max-width: 900px) { display: none; } `;

const NavFooter = styled.div`
  border-top: 1px solid #1f1f24;
  padding-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (max-width: 900px) { display: none; }
`;

const Workspace = styled.main`
  padding: 0;
  min-width: 0; /* prevents grid blowout from wide tables */
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.75rem;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  position: sticky;
  top: 0;
  z-index: 5;
  backdrop-filter: saturate(180%) blur(8px);
  -webkit-backdrop-filter: saturate(180%) blur(8px);
  background: rgba(255, 255, 255, 0.85);
  gap: 1rem;
  flex-wrap: wrap;
  animation: ${enterUp} 280ms var(--ease-out) both;

  h1 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: -0.015em;
    line-height: 1.2;
  }
  .sub {
    margin: 1px 0 0;
    font-size: 0.8rem;
    color: var(--ink-mute);
  }
`;

const Content = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;

  @media (max-width: 700px) {
    padding: 1rem;
  }
`;

// Aliases kept for code paths still using these names. They simply forward
// to the new visuals so the render below didn't have to be rewritten.
const Header = TopBar;
const Brand = styled.div``;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color var(--dur-press) var(--ease-out),
    border-color var(--dur-press) var(--ease-out),
    color var(--dur-press) var(--ease-out),
    box-shadow var(--dur-press) var(--ease-out),
    transform var(--dur-press) var(--ease-out);

  &:active:not(:disabled) { transform: scale(0.97); }
  &:focus-visible { outline: none; box-shadow: var(--shadow-focus); }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: var(--surface-2);
      border-color: #d4d4d8;
      color: var(--ink);
    }
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &.primary {
    background: var(--ink);
    color: white;
    border-color: var(--ink);
  }
  @media (hover: hover) and (pointer: fine) {
    &.primary:hover:not(:disabled) { background: #24242b; }
    &.danger:hover:not(:disabled) {
      background: var(--danger-soft);
      color: var(--danger);
      border-color: #fecaca;
    }
  }
`;

// The old <Tabs>/<TabButton> are no longer rendered. Kept as null-render
// aliases so any stray reference in the render below doesn't crash. The
// nav has moved entirely into the Sidebar.
const Tabs = styled.nav` display: none; `;
const TabButton = styled.button` display: none; `;

const TabPanel = styled.section`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 1.25rem 1.375rem;
  /* No drop shadow — relies on the 1px border for separation. Sharp + quiet. */
  animation: ${enterUp} var(--dur-panel) var(--ease-out) both;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg { color: var(--ink-mute); }
`;

// The Overview answers one question first: what do I do next. Queues someone
// owes work on sit at the top, oldest work first, each tile with its own verb.
// Under them, one card per area of the warehouse (store packages, supplier
// packages, stock), each a door to the page where that work is done.
const QueueHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.875rem;

  h2 { margin: 0; }
  .sub {
    font-size: 0.78rem;
    color: var(--ink-mute);
  }
`;

const QueueList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;

  > li { min-width: 0; display: flex; }
`;

// Tone is how long the oldest item has been waiting, not how many there are:
// three orders stuck for a week matter more than forty that arrived this
// morning. The rail carries the tone; the age pill says it in words, so the
// colour is never the only signal.
const QUEUE_TONES = {
  late: { rail: 'var(--danger)', ink: 'var(--danger)', soft: 'var(--danger-soft)' },
  aging: { rail: 'var(--warn)', ink: '#b45309', soft: 'var(--warn-soft)' },
  fresh: { rail: 'var(--accent)', ink: 'var(--accent-ink)', soft: 'var(--accent-soft)' },
};

const QueueRow = styled.button`
  appearance: none;
  font: inherit;
  text-align: left;
  width: 100%;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.85rem 0.9rem 0.85rem 1.05rem;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: inset 3px 0 0 ${p => QUEUE_TONES[p.$tone].rail};
  transition: background-color var(--dur-hover) var(--ease-out);

  @media (hover: hover) and (pointer: fine) {
    &:hover { background: var(--surface-2); }
  }
  &:active { transform: scale(0.99); }
  &:focus-visible {
    outline: none;
    box-shadow: inset 3px 0 0 ${p => QUEUE_TONES[p.$tone].rail}, 0 0 0 3px rgba(91, 141, 239, 0.25);
  }

  .count {
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.035em;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
  }
  .title {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .title svg { color: var(--ink-mute); flex: none; }
  .age {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.12rem 0.5rem;
    border-radius: 999px;
    color: ${p => QUEUE_TONES[p.$tone].ink};
    background: ${p => QUEUE_TONES[p.$tone].soft};
    white-space: nowrap;
  }
  .act {
    margin-top: auto;
    padding-top: 0.2rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--accent-ink);
  }
`;

const AllClear = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.25rem;
  border: 1px dashed var(--line);
  border-radius: 10px;
  color: var(--ok);
  font-weight: 500;
  font-size: 0.9rem;
`;

// The three doors on the Overview. Each is a summary of one page and a button
// to it: the work happens on the page, the card only says whether to go there.
const DoorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const DoorCard = styled.button`
  appearance: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  transition:
    border-color var(--dur-hover) var(--ease-out),
    background-color var(--dur-hover) var(--ease-out);

  @media (hover: hover) and (pointer: fine) {
    &:hover { border-color: #c9d4f5; background: #fbfcff; }
  }
  &:focus-visible { outline: none; box-shadow: var(--shadow-focus); }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--ink);
  }
  .head svg { color: var(--ink-mute); }
  .open {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--accent-ink);
  }
  .open svg { color: var(--accent-ink); }
  .big { display: flex; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; }
  .big strong {
    font-size: 1.9rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.035em;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
  }
  .big span { font-size: 0.8rem; color: var(--ink-soft); }
  .mini { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.5rem; }
  .mini > div {
    min-width: 0;
    background: var(--surface-2);
    border: 1px solid var(--line-soft);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
  }
  .mini b { display: block; font-size: 1rem; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--ink); }
  .mini span { font-size: 0.72rem; color: var(--ink-soft); }
  .lines { display: flex; flex-direction: column; }
  .lines > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0;
    border-top: 1px solid var(--line-soft);
    font-size: 0.8rem;
    color: var(--ink-2);
  }
  .lines > div > span:first-child { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .empty { font-size: 0.8rem; color: var(--ink-mute); }
`;

const TAG_TONES = {
  blue: { bg: 'var(--accent-soft)', ink: 'var(--accent-ink)' },
  warn: { bg: 'var(--warn-soft)', ink: '#b45309' },
  red: { bg: 'var(--danger-soft)', ink: 'var(--danger)' },
  ok: { bg: 'var(--ok-soft)', ink: 'var(--ok)' },
  grey: { bg: 'var(--surface-2)', ink: 'var(--ink-soft)' },
};
const Tag = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
  background: ${p => TAG_TONES[p.$tone || 'grey'].bg};
  color: ${p => TAG_TONES[p.$tone || 'grey'].ink};
`;

const Crumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: var(--ink-mute);

  button {
    appearance: none;
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    color: var(--accent-ink);
    cursor: pointer;
  }
  button:focus-visible { outline: none; box-shadow: var(--shadow-focus); border-radius: 4px; }
`;

const StoreList = styled.div`
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
`;

const STORE_COLS = 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 0.8fr) 18px';

const StoreHead = styled.div`
  display: grid;
  grid-template-columns: ${STORE_COLS};
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--surface-2);
  font-size: 0.72rem;
  color: var(--ink-mute);

  @media (max-width: 700px) { display: none; }
`;

const StoreRow = styled.button`
  appearance: none;
  border: 0;
  border-top: 1px solid var(--line-soft);
  font: inherit;
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: ${STORE_COLS};
  gap: 0.75rem;
  align-items: center;
  padding: 0.8rem 1rem;
  background: ${p => (p.$open ? 'var(--accent-soft)' : 'var(--surface)')};
  color: var(--ink-2);
  font-size: 0.85rem;
  transition: background-color var(--dur-hover) var(--ease-out);

  @media (hover: hover) and (pointer: fine) {
    &:hover { background: ${p => (p.$open ? 'var(--accent-soft)' : 'var(--surface-2)')}; }
  }
  &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ink); }

  .name { font-weight: 600; color: var(--ink); }
  .none { color: var(--ink-mute); }
  .chev {
    color: var(--ink-mute);
    transform: rotate(${p => (p.$open ? 180 : 0)}deg);
    transition: transform var(--dur-hover) var(--ease-out);
  }

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr) auto auto 18px;
    .waiting, .coming { display: none; }
  }
`;

const StoreBox = styled.div`
  padding: 1rem 1.1rem 1.1rem;
  background: var(--surface-2);
  border-top: 1px solid var(--line-soft);

  .gh {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-mute);
    margin: 0.9rem 0 0.3rem;
  }
  .gh:first-child { margin-top: 0; }
  label.it {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.3rem 0;
    font-size: 0.84rem;
    color: var(--ink-2);
    cursor: pointer;
  }
  .it input { accent-color: var(--accent); width: 15px; height: 15px; flex: none; }
  .it strong { color: var(--ink); font-weight: 600; }
  .soft { color: var(--ink-soft); }
  .foot {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.9rem;
    padding-top: 0.9rem;
    border-top: 1px solid var(--line);
  }
  .note { font-size: 0.78rem; color: var(--ink-soft); }
  .pk {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
    padding: 0.35rem 0;
    font-size: 0.84rem;
  }
  .pk .grow { flex: 1; }
`;

const OutBox = styled.div`
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  padding: 0.7rem 0.85rem;

  & + & { margin-top: 0.5rem; }

  .top { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
  .num { font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
  .meta { font-size: 0.78rem; color: var(--ink-soft); }
  .grow { flex: 1; }
  .contents { margin-top: 0.6rem; border-top: 1px solid var(--line-soft); }
  .line {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.3rem 0;
    border-bottom: 1px solid var(--line-soft);
    font-size: 0.8rem;
    color: var(--ink-2);
  }
  .line b { font-variant-numeric: tabular-nums; color: var(--ink); }
  .hint { margin-top: 0.5rem; font-size: 0.75rem; color: var(--ink-soft); }
`;

const PageNotice = styled.div`
  padding: 0.65rem 0.9rem;
  border-radius: 8px;
  font-size: 0.84rem;
  background: ${p => (p.$kind === 'success' ? 'var(--ok-soft)' : 'var(--danger-soft)')};
  color: ${p => (p.$kind === 'success' ? '#166534' : '#991b1b')};
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;

  th, td {
    padding: 0.65rem 0.875rem;
    text-align: left;
    border-bottom: 1px solid var(--line-soft);
  }
  th {
    background: transparent;
    color: var(--ink-mute);
    font-size: 0.72rem;
    letter-spacing: 0;
    text-transform: none;
    font-weight: 500;
    border-bottom: 1px solid var(--line);
  }
  tr {
    transition: background-color var(--dur-press) var(--ease-out);
  }
  @media (hover: hover) and (pointer: fine) {
    tbody tr:hover { background: var(--surface-2); }
  }
  tr:last-child td { border-bottom: 0; }
  td { color: var(--ink-2); }
  td strong { color: var(--ink); font-weight: 600; }
  td.muted {
    color: var(--ink-mute);
    font-style: normal;
    text-align: center;
    padding: 2.5rem 0;
    font-size: 0.85rem;
  }
  td[data-numeric], td:has(strong[style*="monospace"]) { font-variant-numeric: tabular-nums; }
`;

// An empty table used to be one grey sentence in a cell, which on a quiet
// morning reads as "something failed to load" rather than "you are caught up".
// Icon, a headline that states the fact, and a line naming the next action —
// the point is that a warehouse user seeing this screen for the first time
// learns what to do from it.
// Outline-style status pill — colored dot + label, light tint background.
// Quieter than the previous solid pill while still readable at a glance.
// ─── Lens requests as a grid ─────────────────────────────────────────────────
//
// A store ordering one lens across five powers arrives as five rows that all
// say the same product name. Grouped into the same SPH × CYL grid the stock
// pages use, it reads as one lens with five filled cells.
//
// Only the powers actually requested get an axis, so the grid stays as small as
// the request — three cells stay three cells rather than becoming 49 × 17.
const REQ_STATUS_TONE = {
  supplier_order_required: { bg: '#fef3c7', color: '#92400e', label: 'needs supplier order' },
  supplier_ordered:        { bg: '#dbeafe', color: '#1e40af', label: 'ordered from supplier' },
  supplier_arrived:        { bg: '#e0e7ff', color: '#3730a3', label: 'arrived at central' },
  central_stock_reserved:  { bg: '#dcfce7', color: '#166534', label: 'reserved from central stock' },
  ready_for_package:       { bg: '#dcfce7', color: '#166534', label: 'ready to package' },
  cancelled:               { bg: '#fee2e2', color: '#991b1b', label: 'cancelled' },
};
const reqTone = (status) => REQ_STATUS_TONE[status] || { bg: '#f1f5f9', color: '#475569', label: String(status || '').replace(/_/g, ' ') };

const GRID_SPH_KEYS = new Set(LENS_SPH_VALUES.map(lensKey));
const GRID_CYL_KEYS = new Set(LENS_CYL_VALUES.map(lensKey));

const READY_STATUSES = ['central_stock_reserved', 'supplier_arrived', 'ready_for_package'];

// The request grid is the same sheet as the Lens Stock matrix, so it is sized
// off the same constants and reads the same way — same cell size, same yellow
// header, same red/grey/green SPH spine. Two views of one thing should not look
// like two different products.
const REQ_MATRIX_W = SPH_COL_W + (CELL_W * LENS_CYL_VALUES.length);
const REQ_CARD_PAD = 14;

// Cards hug their table and wrap, so several lenses sit side by side instead of
// one per full-width row.
const ReqGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const ReqGroup = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  flex: 0 1 auto;
  max-width: 100%;
`;

// ── Package surfaces, matching the store's Packages tab ──────────────────────
//
// A package looks the same whichever direction it travels. These mirror the
// store side's Card / list-row / pill exactly — same radius, same double
// shadow, same 0.9375rem title with its icon, same tinted rows — so a manager
// moving between the two screens is reading one design, not two.
const PRIMARY = '#2563eb';

const PkgCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 1.25rem 1.375rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 16px 32px rgba(0, 0, 0, 0.1);
    }
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }

  h3 {
    margin: 0 0 0.375rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    letter-spacing: -0.01em;
  }
  h3 .num {
    margin-left: auto;
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
  }
  p.sub {
    margin: 0 0 0.875rem;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
  }
`;

const PkgRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5625rem 0.75rem;
  border: 1px solid ${PRIMARY}14;
  border-radius: 12px;
  background: rgba(248, 255, 252, 0.7);

  strong { color: #0f172a; font-size: 0.875rem; }
  .meta { color: #94a3b8; font-size: 0.75rem; margin-top: 1px; }
`;

const PkgPill = styled.span`
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${p => (p.$tone === 'ready'
    ? 'linear-gradient(135deg,#f0fdf4,#dcfce7)'
    : p.$tone === 'waiting'
      ? 'linear-gradient(135deg,#fffbeb,#fef3c7)'
      : 'linear-gradient(135deg,#eff6ff,#dbeafe)')};
  color: ${p => (p.$tone === 'ready' ? '#166534' : p.$tone === 'waiting' ? '#92400e' : '#2563eb')};
  border: 1px solid ${p => (p.$tone === 'ready' ? '#bbf7d0' : p.$tone === 'waiting' ? '#fde68a' : '#bfdbfe')};
`;

const PkgNotice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.875rem;
  background: ${p => (p.kind === 'success'
    ? `linear-gradient(135deg, ${PRIMARY}14, ${PRIMARY}0D)`
    : 'linear-gradient(135deg, #fef2f2, #fde8e8)')};
  color: ${p => (p.kind === 'success' ? '#166534' : '#991b1b')};
  border: 1px solid ${p => (p.kind === 'success' ? `${PRIMARY}66` : '#fecaca')};
`;

const PkgList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const ReqGroupHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.6rem ${REQ_CARD_PAD}px;
  margin-bottom: 0.6rem;
  border-bottom: 1px solid #f1f5f9;
  background: #fcfdfe;
`;

const ReqTableWrap = styled.div`
  overflow: auto;
  max-height: 74vh;
  width: max-content;
  max-width: 100%;
  padding: 0 ${REQ_CARD_PAD}px ${REQ_CARD_PAD}px;
`;

const ReqTable = styled.table`
  min-width: ${REQ_MATRIX_W}px;
  width: ${REQ_MATRIX_W}px;
  border-collapse: separate;
  border-spacing: 0;

  th, td {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 0;
    text-align: center;
  }

  /* Row highlight on hover — in a 49-row grid you otherwise count cells to
     work out which power you are looking at. */
  tbody tr:hover th {
    box-shadow: inset 0 0 0 2px #4f46e5;
  }

  thead th {
    background: #fefce8;
    color: #334155;
    height: ${CELL_H}px;
    font-size: 0.6rem;
    font-weight: 800;
  }
  tbody th {
    width: ${SPH_COL_W}px;
    font-size: 0.62rem;
    font-weight: 800;
    color: #fff;
  }
`;

// Stock movements → open store: the shelf and the requests sit side by side,
// so the cells are narrower than the full-size sheet.
const CompactReqTable = styled(ReqTable)`
  width: auto;
  min-width: 0;

  td > div, td > button { min-width: 20px; }
  thead th { font-size: 0.55rem; min-width: 20px; }
`;

// Same spine colouring as the stock matrix: plus green, zero grey, minus red.
const ReqSphHeader = styled.th`
  background: ${p => {
    if (p.$value > 0) return '#22c55e';
    if (p.$value === 0) return '#94a3b8';
    return '#ef4444';
  }} !important;
  border-right-color: ${p => {
    if (p.$value > 0) return '#15803d';
    if (p.$value === 0) return '#64748b';
    return '#991b1b';
  }} !important;
`;

const ReqCell = styled.button`
  min-width: ${CELL_W}px;
  min-height: ${CELL_H}px;
  width: 100%;
  border: 0;
  font-size: 0.64rem;
  font-weight: 800;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  cursor: ${p => (p.$selectable ? 'pointer' : 'default')};
  box-shadow: ${p => (p.$picked ? 'inset 0 0 0 2px #4f46e5' : 'none')};
  transition: 0.1s ease;

  &:hover {
    ${p => p.$selectable && css`
      transform: scale(1.05);
      box-shadow: inset 0 0 0 2px #4f46e5;
      z-index: 2;
      position: relative;
    `}
  }
`;

const ReqEmptyCell = styled.div`
  min-width: ${CELL_W}px;
  min-height: ${CELL_H}px;
  background: ${p => p.$bg || '#fff'};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.18rem 0.55rem 0.18rem 0.5rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0;
  background: ${(p) => p.bg || 'var(--surface-2)'};
  color: ${(p) => p.color || 'var(--ink-2)'};
  border: 1px solid ${(p) => (p.color ? `${p.color}22` : 'var(--line)')};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.color || 'var(--ink-mute)'};
    flex-shrink: 0;
  }
`;

// Status → pill color map (kept short on purpose). Labels come from i18n.
const STATUS_PILL = {
  confirmed:              { labelKey: 'orderStatus.confirmed',              bg: '#dcfce7', color: '#166534' },
  packaged_at_store:      { labelKey: 'orderStatus.packaged_at_store',      bg: '#fef3c7', color: '#92400e' },
  in_transit_to_central:  { labelKey: 'orderStatus.in_transit_to_central',  bg: '#dbeafe', color: '#1e40af' },
  at_central_inbound:     { labelKey: 'orderStatus.at_central_inbound',     bg: '#fce7f3', color: '#9d174d' },
  at_lab:                 { labelKey: 'orderStatus.at_lab',                 bg: '#ede9fe', color: '#5b21b6' },
  in_production:          { labelKey: 'orderStatus.in_production',          bg: '#ede9fe', color: '#5b21b6' },
  manufacturing:          { labelKey: 'orderStatus.manufacturing',          bg: '#ede9fe', color: '#5b21b6' },
  quality_check:          { labelKey: 'orderStatus.quality_check',          bg: '#cffafe', color: '#155e75' },
  returning_to_central:   { labelKey: 'orderStatus.returning_to_central',   bg: '#fef3c7', color: '#92400e' },
  at_central_outbound:    { labelKey: 'orderStatus.at_central_outbound',    bg: '#fce7f3', color: '#9d174d' },
  in_transit_to_store:    { labelKey: 'orderStatus.in_transit_to_store',    bg: '#dbeafe', color: '#1e40af' },
  ready_for_pickup:       { labelKey: 'orderStatus.ready_for_pickup',       bg: '#dcfce7', color: '#166534' },
  delivered:              { labelKey: 'orderStatus.delivered',              bg: '#d1fae5', color: '#065f46' },
  cancelled:              { labelKey: 'orderStatus.cancelled',              bg: '#fee2e2', color: '#991b1b' },
};

const TAB_DEFS = [
  { id: 'overview',     labelKey: 'central.tabOverview',     icon: Layers },
  { id: 'store-packages', labelKey: 'central.tabStorePackages', icon: Warehouse },
  { id: 'supplier-packages', labelKey: 'central.tabSupplierPackages', icon: Truck },
  { id: 'stock-movements', labelKey: 'central.tabStockMovements', icon: Boxes },
  { id: 'activity',     labelKey: 'central.tabActivity',     icon: History },
  { id: 'stock-requests', labelKey: 'central.tabStockRequests', icon: ListChecks },
  { id: 'supplier-lens-orders', labelKey: 'central.tabLensOrderLists', icon: ShoppingCart },
  { id: 'lens-tasks',   labelKey: 'central.tabLensTasks',   icon: ShoppingCart },
  { id: 'supplier-receipt', labelKey: 'central.tabSupplierDelivery', icon: Truck },
  { id: 'incoming',     labelKey: 'central.tabIncoming',     icon: Inbox },
  { id: 'returns',      labelKey: 'central.tabReturns',      icon: SafeOutbox },
  { id: 'outgoing',     labelKey: 'central.tabOutgoing',     icon: SafeOutbox },
  { id: 'reservations', labelKey: 'central.tabReservations', icon: Clock },
  { id: 'lens-stock',   labelKey: 'central.tabLensStock',   icon: Glasses },
  { id: 'frame-stock',  labelKey: 'central.tabFrameStock',  icon: PackageIcon },
  { id: 'accessories',  labelKey: 'central.tabAccessories',  icon: ShoppingBag },
  { id: 'consumables',  labelKey: 'central.tabConsumables',  icon: Boxes },
];

const isKnownTab = (id) => TAB_DEFS.some((tab) => tab.id === id);

// Task switch behind each tab (Overview and Activity are open to anyone who
// can open this dashboard). Roles that reach the dashboard without any MG
// Central default task — couriers — keep every tab, as before switches.
const TAB_TASKS = {
  'store-packages': 'central.transfers',
  'supplier-packages': 'central.supplier',
  'stock-requests': 'central.transfers',
  'incoming': 'central.transfers',
  'outgoing': 'central.transfers',
  'returns': 'central.transfers',
  'reservations': 'central.transfers',
  'supplier-lens-orders': 'central.supplier',
  'lens-tasks': 'central.supplier',
  'supplier-receipt': 'central.supplier',
  'lens-stock': 'central.lenses',
  'frame-stock': 'central.frames',
  'accessories': 'central.supplies',
  'consumables': 'central.supplies',
};
const CENTRAL_DASHBOARD_ROLES = ['inventory_manager', 'operations_manager', 'courier'];

// Thirteen items in one undifferentiated run is a list you read top to bottom
// every time instead of aiming at. Four runs, ordered the way the work actually
// moves — things arrive, things leave, things come back — so you navigate by
// region rather than by scanning labels.
const NAV_GROUPS = [
  // The three Overview cards open these pages, so they sit right under it.
  { id: 'home', labelKey: null, items: ['overview', 'store-packages', 'supplier-packages', 'stock-movements', 'activity'] },
  { id: 'requests', labelKey: 'central.navRequests', items: ['stock-requests', 'supplier-lens-orders', 'lens-tasks'] },
  // Supplier Delivery leads the group: it is the other thing that arrives at
  // central, alongside Incoming (which is packages from stores).
  { id: 'shipping', labelKey: 'central.navShipping', items: ['supplier-receipt', 'incoming', 'outgoing', 'returns'] },
  { id: 'inventory', labelKey: 'central.navInventory', items: ['reservations', 'lens-stock', 'frame-stock', 'accessories', 'consumables'] },
];

// Badges that mean "someone owes work here" versus badges that just count what
// exists. Same rule as the Overview tiles: if everything glows, nothing does.
const WORK_TABS = new Set(['stock-requests', 'supplier-lens-orders', 'lens-tasks', 'incoming', 'supplier-receipt']);

// Status sets that map to each pipeline phase.
const INCOMING_STATUSES = ['confirmed', 'packaged_at_store', 'in_transit_to_central', 'at_central_inbound'];
const OUTGOING_STATUSES = ['returning_to_central', 'at_central_outbound', 'in_transit_to_store'];

const LENS_FULFILLMENT_PILL = {
  reserved_waiting_inventory: { labelKey: 'central.lensStatusReserved', bg: '#dbeafe', color: '#1e40af' },
  lens_ready_waiting_frame: { labelKey: 'central.lensStatusReady', bg: '#dcfce7', color: '#166534' },
  supplier_order_required: { labelKey: 'central.lensStatusNeedSupplier', bg: '#fee2e2', color: '#991b1b' },
  lens_ordered_waiting_supplier: { labelKey: 'central.lensStatusWaitingSupplier', bg: '#fef3c7', color: '#92400e' },
  lens_arrived_waiting_frame: { labelKey: 'central.lensStatusArrived', bg: '#ccfbf1', color: '#115e59' },
  lens_arrived_equipment_ready: { labelKey: 'central.lensStatusEquipmentReady', bg: '#dcfce7', color: '#166534' },
};

const formatDateTime = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString();
  } catch (_) { return ''; }
};

const formatCurrency = (n) => {
  if (n == null) return '';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
};

const formatStatusLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

// ─── Modal styles ───────────────────────────────────────────────────
const backdropFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// Modal scale starts at 0.95 — never from 0. Nothing in the real world
// appears from nothing. Combined with opacity to make the entry feel
// physical rather than teleporting.
const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 15, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 100;
  animation: ${backdropFade} 200ms var(--ease-out) both;
`;

const ModalShell = styled.div`
  width: 100%;
  max-width: 880px;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--surface);
  border-radius: 14px;
  border: 1px solid var(--line);
  box-shadow: var(--shadow-pop);
  display: flex;
  flex-direction: column;
  animation: ${modalIn} var(--dur-modal) var(--ease-out) both;
`;

const ModalHead = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--line);
  position: sticky;
  top: 0;
  background: var(--surface);
  z-index: 1;
  border-radius: 14px 14px 0 0;

  h2 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--ink);
    letter-spacing: -0.015em;
  }
  .sub {
    margin-top: 4px;
    font-size: 0.82rem;
    color: var(--ink-soft);
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--line);
  position: sticky;
  bottom: 0;
  background: var(--surface);
  border-radius: 0 0 14px 14px;
`;

const PrimaryAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--ink);
  background: var(--ink);
  color: white;
  font-weight: 500;
  font-size: 0.85rem;
  cursor: pointer;
  transition:
    background-color var(--dur-press) var(--ease-out),
    transform var(--dur-press) var(--ease-out),
    box-shadow var(--dur-press) var(--ease-out);

  &:active:not(:disabled) { transform: scale(0.97); }
  &:focus-visible { outline: none; box-shadow: var(--shadow-focus); }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) { background: #24242b; }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const OrderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${(p) => (p.opened ? 'rgba(22, 163, 74, 0.25)' : 'var(--line)')};
  background: ${(p) => (p.opened ? 'rgba(22, 163, 74, 0.04)' : 'var(--surface)')};
  border-radius: 10px;
  transition: background-color var(--dur-press) var(--ease-out), border-color var(--dur-press) var(--ease-out);

  .num {
    font-family: ui-monospace, monospace;
    font-weight: 600;
    color: var(--ink);
    font-size: 0.92rem;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }
  .meta {
    color: var(--ink-mute);
    font-size: 0.78rem;
  }
  .right {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

const TinyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--surface);
  color: var(--ink-2);
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 0.3rem 0.55rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.76rem;
  transition: background-color var(--dur-press) var(--ease-out), transform var(--dur-press) var(--ease-out), color var(--dur-press) var(--ease-out);

  &:active:not(:disabled) { transform: scale(0.96); }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: var(--surface-2);
      color: var(--ink);
      border-color: #d4d4d8;
    }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CentralStoreDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tabLabel = (tabOrId) => {
    const def = typeof tabOrId === 'string'
      ? TAB_DEFS.find((tab) => tab.id === tabOrId)
      : tabOrId;
    return def?.labelKey ? t(def.labelKey) : (def?.id || '');
  };
  const groupLabel = (group) => (group.labelKey ? t(group.labelKey) : null);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
  }, []);

  // The label's route band names both ends. The signed-in warehouse is the
  // origin for everything printed from this screen.
  const { branding } = useBranding();
  const centralStoreName = user.store?.name || user.store?.storeCode || 'Central';

  // The tab lives in the URL, not just in state. It was only ever *read* from
  // the query string on first mount and never written back, so a reload — or
  // the back button — dropped you on Overview no matter where you were.
  const location = useLocation();
  const tabFromUrl = new URLSearchParams(location.search).get('tab');
  const tabOk = useCallback((id) => !TAB_TASKS[id] || canAccess(user, TAB_TASKS[id], CENTRAL_DASHBOARD_ROLES), [user]);
  const activeTab = isKnownTab(tabFromUrl) && tabOk(tabFromUrl) ? tabFromUrl : 'overview';

  const setActiveTab = useCallback((id) => {
    if (!isKnownTab(id) || !tabOk(id)) return;
    if (id === activeTab) return;
    const params = new URLSearchParams(window.location.search);
    params.set('tab', id);
    // push, not replace. Once the tab lives in the URL, Back is expected to
    // step back through tabs; replacing meant the first Back press left the
    // dashboard entirely and dropped you on the sign-in screen.
    navigate({ search: `?${params.toString()}` });
  }, [navigate, activeTab, tabOk]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [outgoingPackages, setOutgoingPackages] = useState([]);
  const [lensTasks, setLensTasks] = useState([]);
  const [supplierLensBatches, setSupplierLensBatches] = useState([]);
  // Whoever places the supplier order tends to always want the same reading, so
  // the choice sticks rather than resetting on every visit.
  const [supplierLensView, setSupplierLensViewState] = useState(() => {
    try { return localStorage.getItem('optiva.supplierLensView') === 'matrix' ? 'matrix' : 'list'; } catch (_) { return 'list'; }
  });
  const setSupplierLensView = (next) => {
    setSupplierLensViewState(next);
    try { localStorage.setItem('optiva.supplierLensView', next); } catch (_) { /* private mode — the toggle still works for this session */ }
  };
  const [supplierLensTotals, setSupplierLensTotals] = useState({ orders: 0, lenses: 0 });
  const [lensTaskBusyId, setLensTaskBusyId] = useState(null);
  const [supplierOrderBusyId, setSupplierOrderBusyId] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [inboundPackages, setInboundPackages] = useState([]); // status='at_central'
  const [returningOrders, setReturningOrders] = useState([]); // status='returning_to_central'
  // Accessories waiting to travel to a store. Third category in the one box,
  // alongside customer orders and lens transfers.
  const [packageableSupplies, setPackageableSupplies] = useState([]);
  const [pickedReturnSupplyIds, setPickedReturnSupplyIds] = useState({});
  const [pickedReturnIds, setPickedReturnIds] = useState({}); // orderId → bool
  const [pickedReturnTransferIds, setPickedReturnTransferIds] = useState({});
  const [returnBusy, setReturnBusy] = useState(false);
  const [returnNotice, setReturnNotice] = useState(null); // { kind, message, package? }
  const [stockRequests, setStockRequests] = useState([]);
  // Count only, so the Supplier Delivery tab can say how many lines are waiting
  // without the sidebar having to mount the panel that lists them.
  const [supplierOutstanding, setSupplierOutstanding] = useState(0);
  const [supplierLines, setSupplierLines] = useState([]);
  // Packages stores have sealed or put on the road to central, not yet here.
  const [storePackagesIn, setStorePackagesIn] = useState([]);
  // Per-location stock, open requests and the movement ledger (null until loaded).
  const [stockOverview, setStockOverview] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [openStoreId, setOpenStoreId] = useState(null);
  // Store-page items unticked by hand; everything ready starts ticked.
  const [leftOut, setLeftOut] = useState({});
  const [storeBusy, setStoreBusy] = useState(null);
  const [storeNotice, setStoreNotice] = useState(null);
  const [openBoxIds, setOpenBoxIds] = useState({});
  // Stock movements: the store opened under the table (its shelf next to what
  // it asked central for), which of its products are open, lenses or frames.
  const [stockStore, setStockStore] = useState(null); // { storeId, name, loading, stock, requests, error }
  const [openStockProducts, setOpenStockProducts] = useState(() => new Set());
  const [stockKind, setStockKind] = useState('lenses');
  const [stockTransferBusy, setStockTransferBusy] = useState(null);
  const [stockNotice, setStockNotice] = useState(null);
  // Stock movements download: period in days and the in-flight flag.
  const [movesDays, setMovesDays] = useState(7);
  const [movesDownloading, setMovesDownloading] = useState(false);
  // Stores opened in Incoming orders; every store starts folded.
  const [openIncomingStores, setOpenIncomingStores] = useState(() => new Set());
  // Products opened in Lens Order Lists; every product starts folded.
  const [openOrderLensGroups, setOpenOrderLensGroups] = useState(() => new Set());
  // Stores opened in Lens Tasks; every store starts folded.
  const [openLensStores, setOpenLensStores] = useState(() => new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchNotice, setBatchNotice] = useState(null);
  const [stockRequestNotice, setStockRequestNotice] = useState(null);
  const [stockRequestBusy, setStockRequestBusy] = useState(false);
  const [pickedTransferIds, setPickedTransferIds] = useState({});
  const [openPackages, setOpenPackages] = useState([]);
  const [releasingId, setReleasingId] = useState(null);
  const [reservationNotice, setReservationNotice] = useState(null);
  const [activities, setActivities] = useState([]);
  const [thirdPartyBusyId, setThirdPartyBusyId] = useState(null);
  const [handoffTarget, setHandoffTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // When a screen is legitimately empty, a refresh redraws the same nothing and
  // is indistinguishable from a dead button. This is the proof it ran.
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  // Collapsed rail. Remembered per browser: someone working off a tablet all
  // day shouldn't re-collapse it after every reload.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('centralNavCollapsed') === '1'; } catch { return false; }
  });
  const toggleNav = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('centralNavCollapsed', next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Folded groups, by id. Everything starts open — a rail that hides itself on
  // first run is a rail people think is broken.
  const [foldedGroups, setFoldedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('centralNavFolded') || '[]')); }
    catch { return new Set(); }
  });
  const toggleGroup = useCallback((groupId) => {
    setFoldedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      try { localStorage.setItem('centralNavFolded', JSON.stringify([...next])); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Package-detail modal state. `selectedPackage` holds the deeply
  // populated package once the user clicks a row; `unpacking` shows
  // a spinner on the unpack button.
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDetailLoading, setPackageDetailLoading] = useState(false);
  const [unpacking, setUnpacking] = useState(false);
  const [handoffOrderId, setHandoffOrderId] = useState(null);
  const [unpackNotice, setUnpackNotice] = useState(null); // { kind, message }

  const fetchOrdersByStatus = useCallback(async (statuses) => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    // The /api/orders endpoint accepts a single `status` filter; we
    // fan out and merge so the dashboard can show "all incoming"
    // without changing the server. Limit each query to keep things snappy.
    const calls = statuses.map((s) =>
      axios.get(`/api/orders?status=${encodeURIComponent(s)}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.data?.orders || []).catch(() => [])
    );
    const lists = await Promise.all(calls);
    return lists.flat();
  }, []);

  const fetchReservations = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/reservations?status=active&limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.items || [];
    } catch (err) {
      console.error('Failed to load reservations:', err);
      return [];
    }
  }, []);

  const releaseReservation = useCallback(async (r) => {
    if (!r?._id) return;
    setReleasingId(r._id);
    setReservationNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post('/api/reservations/release', { id: r._id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReservationNotice({
        kind: 'success',
        message: `Released ${resp.data?.releasedQty ?? r.qty} unit(s) of ${r.productName || 'lens'} back into stock.`,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setReservationNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to release.' });
    } finally {
      setReleasingId(null);
    }
  }, []);

  const fetchLensTasks = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/orders/central/lens-tasks', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.orders || [];
    } catch (err) {
      console.error('Failed to load lens tasks:', err);
      return [];
    }
  }, []);

  const fetchSupplierLensOrders = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return { batches: [], totals: { orders: 0, lenses: 0 } };
    try {
      const resp = await axios.get('/api/orders/central/supplier-lens-orders', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        batches: resp.data?.batches || [],
        totals: resp.data?.totals || { orders: 0, lenses: 0 },
      };
    } catch (err) {
      console.error('Failed to load supplier lens order lists:', err);
      return { batches: [], totals: { orders: 0, lenses: 0 } };
    }
  }, []);

  const fetchInboundPackages = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/packages?status=at_central&limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.items || [];
    } catch (err) {
      console.error('Failed to load inbound packages:', err);
      return [];
    }
  }, []);

  const fetchOutgoingPackages = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    const statuses = ['at_central_returned', 'in_transit_to_store'];
    try {
      const calls = statuses.map((status) => axios.get(`/api/packages?status=${status}&limit=80`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.data?.items || []));
      const lists = await Promise.all(calls);
      const byId = new Map();
      lists.flat().forEach((pkg) => byId.set(pkg._id, pkg));
      return Array.from(byId.values()).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
    } catch (err) {
      console.error('Failed to load outgoing packages:', err);
      return [];
    }
  }, []);

  // Fetch the deep-populated package the modal needs (orders + visitor +
  // sales person + items).
  const openPackageDetail = useCallback(async (pkgListItem) => {
    setSelectedPackage({ ...pkgListItem, _loading: true });
    setUnpackNotice(null);
    setPackageDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`/api/packages/${pkgListItem._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedPackage(resp.data?.package || pkgListItem);
    } catch (err) {
      setUnpackNotice({
        kind: 'error',
        message: err.response?.data?.message || 'Failed to load package details.',
      });
    } finally {
      setPackageDetailLoading(false);
    }
  }, []);

  const closePackageDetail = useCallback(() => {
    setSelectedPackage(null);
    setUnpackNotice(null);
    setUnpacking(false);
    setHandoffOrderId(null);
  }, []);

  const unpackSelectedPackage = useCallback(async () => {
    if (!selectedPackage?._id) return;
    setUnpacking(true);
    setUnpackNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `/api/packages/${selectedPackage._id}/unpack`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPackage(resp.data?.package || selectedPackage);
      setUnpackNotice({
        kind: 'success',
        message: `Package opened. Use "Hand over equipment to lab" beside each order when the physical equipment leaves central.`,
      });
      setRefreshKey((k) => k + 1); // refresh dashboards
    } catch (err) {
      setUnpackNotice({
        kind: 'error',
        message: err.response?.data?.message || 'Failed to unpack package.',
      });
    } finally {
      setUnpacking(false);
    }
  }, [selectedPackage]);

  const handoffOrderToLab = useCallback(async (order) => {
    if (!selectedPackage?._id || !order?._id) return;
    setHandoffOrderId(order._id);
    setUnpackNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `/api/packages/${selectedPackage._id}/orders/${order._id}/handoff-to-lab`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPackage(resp.data?.package || selectedPackage);
      setUnpackNotice({
        kind: 'success',
        message: resp.data?.alreadyHanded
          ? `Order ${order.orderNumber} was already handed to the lab.`
          : `Order ${order.orderNumber} equipment handed to lab. It is now waiting for lab receipt scan.`,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setUnpackNotice({
        kind: 'error',
        message: err.response?.data?.message || 'Failed to hand order to lab.',
      });
    } finally {
      setHandoffOrderId(null);
    }
  }, [selectedPackage]);

  // Same rule as the store side: nothing traces an outside handoff except the
  // name given here, so the modal asks for it every time and it cannot be
  // skipped.
  const handoffToThirdPartyCourier = useCallback(async (recipientName) => {
    const pkg = handoffTarget;
    if (!pkg?._id || !recipientName) return;
    setThirdPartyBusyId(pkg._id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/packages/${pkg._id}/third-party-courier`, { recipientName }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHandoffTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to hand package to 3rd party courier.');
    } finally {
      setThirdPartyBusyId(null);
    }
  }, [handoffTarget]);

  const fetchReturningOrders = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/orders/central/returns', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.orders || [];
    } catch (err) {
      console.error('Failed to load returning orders:', err);
      return [];
    }
  }, []);

  const fetchStockRequests = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      // Every open request, however old: the newest 200 left months of ready
      // refills off Store packages, so they were never packed.
      const resp = await axios.get('/api/stock-transfer-requests?open=1', {
        headers: { Authorization: `Bearer ${token}` },
      });
      // This tab is a work queue, not a history. Anything finished, called off,
      // or already on its way is done with here — leaving it on screen is what
      // made the page look full of requests nobody was going to act on.
      const DONE = ['cancelled', 'declined', 'fulfilled', 'received'];
      return (resp.data?.requests || []).filter((r) => {
        if (DONE.includes(String(r.status || ''))) return false;
        // Packed and gone. It is a package being tracked in Outgoing now, not a
        // request central can still do anything about — but the grid kept
        // drawing it, so the page showed cards while the summary above
        // correctly said there was nothing left to send.
        const pkgStatus = r.package?.status;
        if (pkgStatus && pkgStatus !== 'open_at_central') return false;
        return true;
      });
    } catch (err) {
      console.error('Failed to load stock transfer requests:', err);
      return [];
    }
  }, []);

  // Just the number of lines still owed by the supplier, for the sidebar badge.
  // The Supplier Delivery panel loads the lines themselves when it opens.
  const fetchSupplierOutstanding = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/stock-transfer-requests/supplier-outstanding', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.lines || [];
    } catch (err) {
      // Restricted to inventory/ops roles, so a central user without one gets a
      // 403 here. That is not an error worth shouting about — it just means no
      // badge for them.
      return [];
    }
  }, []);

  const fetchStorePackagesIn = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const calls = ['packaged_at_store', 'in_transit_to_central'].map((status) => axios.get(`/api/packages?status=${status}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      }));
      const results = await Promise.all(calls);
      return results.flatMap((r) => r.data?.items || []);
    } catch (err) {
      console.error('Failed to load store packages on their way:', err);
      return [];
    }
  }, []);

  const fetchStockOverview = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const resp = await axios.get('/api/packages/central/stock-overview', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data || null;
    } catch (err) {
      console.error('Failed to load stock overview:', err);
      return null;
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/packages/central/activity?limit=160', {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The endpoint answers { items, count } — `activities` was never a key on
      // the response, so the feed rendered empty every time.
      return resp.data?.items || [];
    } catch (err) {
      console.error('Failed to load central activity:', err);
      return [];
    }
  }, []);

  // Accessories central has agreed to send but that are not in a box yet.
  // `packaged` comes from the join table added in migration 018; until that is
  // run the server reports nothing as packed, so these stay selectable and the
  // create call reports back that they could not be stored.
  const fetchPackageableSupplies = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/supplies/requests?status=open', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return (resp.data?.requests || []).filter((r) => (
        ['ready_for_package', 'approved', 'pending'].includes(r.status) && !r.packaged
      ));
    } catch (err) {
      // A tenant that has never run the supplies migrations answers 503 here.
      // That is a missing optional feature, not a dashboard failure.
      console.warn('Accessories unavailable for packing:', err.response?.status || err.message);
      return [];
    }
  }, []);

  // One open package per store, filling up as things become ready for it.
  const fetchOpenPackages = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const resp = await axios.get('/api/stock-transfer-requests/open-packages', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data?.packages || [];
    } catch (err) {
      console.error('Failed to load open transfer packages:', err);
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    const load = async () => {
      setLoading(true);
      try {
        // Each card fills in as soon as its own data arrives, rather than the
        // whole page waiting for the slowest of fifteen requests. The work
        // queue's sources go first: the browser runs only a handful at once.
        const fill = (promise, apply) => promise.then((value) => { if (!cancelled) apply(value); });
        await Promise.all([
          fill(fetchLensTasks(), setLensTasks),
          fill(fetchStockRequests(), setStockRequests),
          fill(fetchSupplierLensOrders(), (supplierLens) => {
            setSupplierLensBatches(supplierLens.batches);
            setSupplierLensTotals(supplierLens.totals);
          }),
          fill(fetchOrdersByStatus(INCOMING_STATUSES), setIncoming),
          fill(fetchInboundPackages(), setInboundPackages),
          fill(fetchSupplierOutstanding(), (supplierDue) => {
            setSupplierOutstanding(supplierDue.length);
            setSupplierLines(supplierDue);
          }),
          fill(fetchStorePackagesIn(), setStorePackagesIn),
          fill(fetchReturningOrders(), setReturningOrders),
          fill(fetchOutgoingPackages(), setOutgoingPackages),
          fill(fetchStockOverview(), setStockOverview),
          fill(fetchPackageableSupplies(), setPackageableSupplies),
          fill(fetchOrdersByStatus(OUTGOING_STATUSES), setOutgoing),
          fill(fetchReservations(), setReservations),
          fill(fetchActivities(), setActivities),
          fill(fetchOpenPackages(), setOpenPackages),
        ]);
        if (cancelled) return;
        loaded = true;
        setLastUpdatedAt(new Date());
      } catch (err) {
        // Promise.all rejects on the first failure. Without this the rejection
        // escaped as an unhandled promise and nothing said why the screen had
        // gone stale.
        if (!cancelled) console.error('Failed to load central dashboard:', err);
      } finally {
        // Always clear the flag. It used to be skipped whenever the run was
        // cancelled — and since the Refresh button is disabled while loading,
        // one cancelled run left it disabled for good, which is exactly what
        // "Refresh isn't working" looked like.
        setLoading(false);
        if (!loaded && !cancelled) {
          // Reached the finally without ever assigning state: the fetches
          // resolved into nothing usable. Say so rather than showing an empty
          // dashboard that looks like real data.
          console.warn('Central dashboard refresh completed with no data.');
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchOrdersByStatus, fetchLensTasks, fetchSupplierLensOrders, fetchReservations, fetchInboundPackages, fetchOutgoingPackages, fetchReturningOrders, fetchStockRequests, fetchActivities, fetchOpenPackages, fetchSupplierOutstanding, fetchPackageableSupplies, fetchStorePackagesIn, fetchStockOverview, refreshKey]);

  // Lens requests grouped into one grid per (store, product). Everything that
  // isn't a lens has no SPH/CYL to plot, so it stays in the flat list below.
  const lensRequestGroups = useMemo(() => {
    const groups = new Map();
    for (const r of stockRequests) {
      if (r.type !== 'lens') continue;
      const storeId = r.requestingStore?._id || r.requestingStore?.id || r.requestingStore || 'store';
      const key = `${storeId}::${r.productName || 'Lens'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          productName: r.productName || 'Lens',
          sku: r.sku || '',
          storeName: r.requestingStore?.name || 'Store',
          requests: [],
        });
      }
      groups.get(key).requests.push(r);
    }
    // The full SPH/CYL sheet, same as the store's stock table — central reads a
    // request against the whole range, not just the handful of cells ordered.
    return [...groups.values()].map((g) => {
      // A cell can hold more than one request: when central covers part of a
      // line from its own stock and orders the rest, the store's single ask
      // becomes a ready row and a supplier row on the same SPH/CYL. Keying one
      // request per cell dropped the other one off this grid entirely.
      const byCell = new Map();
      let offGrid = 0;
      for (const r of g.requests) {
        const key = `${lensKey(r.sph)}:${lensKey(r.cyl)}`;
        const atCell = byCell.get(key) || [];
        atCell.push(r);
        byCell.set(key, atCell);
        if (!GRID_SPH_KEYS.has(lensKey(r.sph)) || !GRID_CYL_KEYS.has(lensKey(r.cyl))) offGrid += 1;
      }
      return { ...g, byCell, offGrid };
    });
  }, [stockRequests]);

  // Per store: what is still not ready to go. A package can be sent while these
  // are outstanding — they follow in the next one — but central should be able
  // to see exactly what it is leaving behind before pressing Send.
  const notReadyByStore = useMemo(() => {
    const map = new Map();
    for (const r of stockRequests) {
      if (READY_STATUSES.includes(r.status)) continue;
      if (['cancelled', 'declined', 'received', 'fulfilled'].includes(r.status)) continue;
      if (r.package) continue;
      const storeId = r.requestingStore?._id || r.requestingStore?.id || r.requestingStore || 'unknown';
      const list = map.get(storeId) || [];
      list.push(r);
      map.set(storeId, list);
    }
    return map;
  }, [stockRequests]);

  // Everything the Stock Requests tab needs to say about a store, in one row of
  // data: the open box, what is in it, and what is not going with it.
  const outgoingByStore = useMemo(() => {
    const key = (id) => String(id || 'unknown');
    const storeOf = (r) => r.requestingStore?._id || r.requestingStore?.id || r.requestingStore;
    const entries = new Map();
    const entry = (id, name) => {
      const k = key(id);
      if (!entries.has(k)) entries.set(k, { storeId: k, storeName: name || null, pkg: null, ready: [], waiting: [] });
      const e = entries.get(k);
      if (!e.storeName && name) e.storeName = name;
      return e;
    };

    for (const pkg of openPackages) {
      const e = entry(pkg.store?._id || pkg.store?.id, pkg.store?.name);
      e.pkg = pkg;
    }
    // "Ready" now means ready to be PACKED, not already packed. The server no
    // longer opens a box and drops transfers into it the moment they are
    // available — a person builds the box in the Returns tab, so what this tab
    // reports is what is sitting on the bench waiting to be picked up. Older
    // boxes that were auto-opened before that change still carry their lines,
    // so both cases count as ready.
    for (const r of stockRequests) {
      const readyToPack = READY_STATUSES.includes(r.status) && !r.package;
      const inLegacyOpenBox = r.package?.status === 'open_at_central';
      if (!readyToPack && !inLegacyOpenBox) continue;
      entry(storeOf(r), r.requestingStore?.name).ready.push(r);
    }
    for (const [storeId, waiting] of notReadyByStore.entries()) {
      const e = entry(storeId, waiting[0]?.requestingStore?.name);
      e.waiting = waiting;
    }
    // Stores with a box to send come first — that is the only actionable row.
    return [...entries.values()].sort((a, b) => (b.pkg?.itemCount || 0) - (a.pkg?.itemCount || 0));
  }, [openPackages, stockRequests, notReadyByStore]);

  // One line per lens+power+status. Central orders three of the same power as
  // three separate requests, and listing them separately makes a card look
  // busier than the work actually is — you read three rows to learn one fact.
  const groupLines = (list) => {
    const byKey = new Map();
    for (const r of list) {
      const key = [r.productName || 'Lens', r.displayCorrection || '', r.status || ''].join('|');
      const existing = byKey.get(key);
      if (existing) {
        existing.qty += Number(r.qty) || 0;
        existing.ids.push(r._id);
      } else {
        byKey.set(key, { ...r, qty: Number(r.qty) || 0, ids: [r._id] });
      }
    }
    return [...byKey.values()];
  };

  const nonLensRequests = useMemo(() => stockRequests.filter((r) => r.type !== 'lens'), [stockRequests]);

  const packageableStockRequests = useMemo(() => stockRequests.filter((r) => (
    ['central_stock_reserved', 'supplier_arrived', 'ready_for_package'].includes(r.status)
  )), [stockRequests]);

  const packageableLensTransfers = useMemo(() => packageableStockRequests.filter((r) => (
    r.type === 'lens' && !r.package
  )), [packageableStockRequests]);

  // Everything waiting to travel to a store, in one row per store: finished
  // customer orders, lens transfers, and accessories. This grouping is what
  // makes one box possible — the three used to be packed (or not packed) by
  // three unrelated flows, so a store could have two boxes leaving at once and
  // its accessories in neither.
  const returnsByStore = useMemo(() => {
    const map = new Map();
    const entry = (sid, store) => {
      if (!map.has(sid)) map.set(sid, { storeId: sid, store, orders: [], transfers: [], supplies: [] });
      const e = map.get(sid);
      if (!e.store && store) e.store = store;
      return e;
    };
    for (const o of returningOrders) {
      entry(o.store?._id || o.store || 'unknown', o.store).orders.push(o);
    }
    for (const r of packageableLensTransfers) {
      entry(r.requestingStore?._id || r.requestingStore || 'unknown', r.requestingStore).transfers.push(r);
    }
    for (const s of packageableSupplies) {
      // The supplies endpoint carries the destination store's name precisely so
      // an accessory-only group is not headed "Unknown store".
      const store = s.destinationStoreName
        ? { _id: s.destinationStoreId, name: s.destinationStoreName, storeCode: s.destinationStoreCode }
        : null;
      entry(s.destinationStoreId || 'unknown', store).supplies.push(s);
    }
    return Array.from(map.values());
  }, [returningOrders, packageableLensTransfers, packageableSupplies]);

  // `silent` suppresses the per-request notice and reload so a bulk run can
  // report once at the end instead of once per cell.
  const updateStockRequestStatus = useCallback(async (request, status, { silent = false } = {}) => {
    if (!silent) {
      setStockRequestBusy(true);
      setStockRequestNotice(null);
    }
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`/api/stock-transfer-requests/${request._id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!silent) {
        setStockRequestNotice({ kind: 'success', message: `${request.productName} updated to ${status.replace(/_/g, ' ')}.` });
        setRefreshKey((k) => k + 1);
      }
      return { ok: true, releasedQty: Number(data?.releasedQty) || 0 };
    } catch (err) {
      if (!silent) {
        setStockRequestNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to update request.' });
      }
      return { ok: false, releasedQty: 0 };
    } finally {
      if (!silent) setStockRequestBusy(false);
    }
  }, []);

  // Bulk action over a whole grid — one lens across many powers is one decision
  // in practice, not one per cell.
  //
  // Cancelling asks first. A matrix order runs to hundreds of corrections, this
  // is one click, and there is no undo: the requests are cancelled and the units
  // central was holding go back on its shelf. Worth a sentence before it goes.
  const updateGroupStatus = useCallback(async (group, status) => {
    const targets = group.requests.filter((r) => !['received', 'cancelled', 'packaged', 'in_transit'].includes(r.status));
    if (!targets.length) return;

    if (status === 'cancelled') {
      const units = targets.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
      const storeName = group.storeName || 'this store';
      const ok = window.confirm(
        `Cancel ${targets.length} correction(s) — ${units} unit(s) — of ${group.productName || 'this lens'} for ${storeName}?\n\n`
        + 'The store\'s order is cancelled, anything central was holding goes back on its shelf, '
        + 'and the supplier reorders those pulls raised are withdrawn. This cannot be undone.'
      );
      if (!ok) return;
    }

    setStockRequestBusy(true);
    setStockRequestNotice(null);
    const label = status === 'cancelled' ? 'cancelled' : status.replace(/_/g, ' ');

    // Cancelling goes in one request. Looping it per correction meant a 737-cell
    // grid took 737 round trips, and anything that interrupted the page — a
    // reload, navigating away — left the run half-finished, which is what
    // "Cancel all only cancels a few" was.
    if (status === 'cancelled') {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.post('/api/stock-transfer-requests/bulk-status', {
          ids: targets.map((r) => r._id),
          status,
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 });
        const released = Number(data?.releasedQty) || 0;
        const withdrawn = Number(data?.refillsWithdrawn) || 0;
        const back = released > 0 ? ` ${released} unit(s) returned to central stock.` : '';
        const reorders = withdrawn > 0 ? ` ${withdrawn} supplier reorder(s) withdrawn.` : '';
        setStockRequestNotice({
          kind: 'success',
          message: `${data?.updated ?? targets.length} correction(s) cancelled.${back}${reorders}`,
        });
      } catch (err) {
        setStockRequestNotice({
          kind: 'error',
          message: err.response?.data?.message || 'Failed to cancel those requests.',
        });
      } finally {
        setStockRequestBusy(false);
        setRefreshKey((k) => k + 1);
      }
      return;
    }

    // Ordered / Arrived / Ready still go one at a time: each has side effects
    // the bulk route does not carry (joining the store's open package, and
    // rewriting the marker in `notes`).
    let done = 0;
    let failed = 0;
    for (const r of targets) {
      // eslint-disable-next-line no-await-in-loop
      const result = await updateStockRequestStatus(r, status, { silent: true });
      if (result.ok) { done += 1; } else { failed += 1; }
    }
    setStockRequestBusy(false);
    setStockRequestNotice(failed
      ? { kind: 'error', message: `${done} ${label}, ${failed} failed.` }
      : { kind: 'success', message: `${done} correction(s) ${label}.` });
    // One reload for the whole run, not one per correction.
    setRefreshKey((k) => k + 1);
  }, [updateStockRequestStatus]);

  // The same label the store prints — branded route band, QR, checklist. Central
  // used to have its own plainer version with no logo and no direction on it.
  const printTransferPackageLabel = useCallback(async (pkg, { supplies = [] } = {}) => {
    if (!pkg) return;
    let full = pkg;
    // The list view carries enough to display, not enough to print: the QR image
    // and the destination store's branding only come back on the detail route.
    if (!pkg.qrDataUrl || !pkg.originStore?.branding) {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`/api/packages/${pkg._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        full = data?.package || pkg;
      } catch (err) {
        console.warn('Failed to load full package for print:', err);
      }
    }
    openPrintIframe(buildPackageLabelHtml(supplies.length ? { ...full, supplies } : full, {
      branding,
      defaultBranding: DEFAULT_BRANDING,
      centralStoreName: centralStoreName || 'Central',
    }));
  }, [branding, centralStoreName]);

  // Ready items are already in the store's open package — nothing to assemble.
  // Sending is what central does: it closes the box and hands it to the courier
  // leg, and the next thing that becomes ready opens a fresh one.
  const sendOpenPackage = useCallback(async (pkg) => {
    if (!pkg?._id) return;
    setStockRequestBusy(true);
    setStockRequestNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`/api/stock-transfer-requests/open-packages/${pkg._id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sent = resp.data?.package;
      // No printing from here. The label belongs to the box the courier picks
      // up, and that box can still grow — printing now would put a stale item
      // count on the shelf. Print from Outgoing, once, when it goes.
      setStockRequestNotice({
        kind: 'success',
        message: resp.data?.merged
          ? `Added to ${sent?.packageNumber} for ${pkg.store?.name || 'store'} — now ${sent?.itemCount} item(s). Print it from Outgoing when the courier comes.`
          : `Package ${sent?.packageNumber || pkg.packageNumber} moved to Outgoing for ${pkg.store?.name || 'store'} with ${sent?.itemCount ?? pkg.itemCount} item(s).`,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setStockRequestNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to send package.' });
    } finally {
      setStockRequestBusy(false);
    }
  }, []);

  const pickedCountForStore = useCallback((storeId) => {
    const orderCount = returningOrders.filter(
      (o) => (o.store?._id || o.store) === storeId && pickedReturnIds[o._id]
    ).length;
    const transferCount = packageableLensTransfers.filter(
      (r) => (r.requestingStore?._id || r.requestingStore) === storeId && pickedReturnTransferIds[r._id]
    ).length;
    const supplyCount = packageableSupplies.filter(
      (s) => s.destinationStoreId === storeId && pickedReturnSupplyIds[s._id]
    ).length;
    return orderCount + transferCount + supplyCount;
  }, [returningOrders, pickedReturnIds, packageableLensTransfers, pickedReturnTransferIds,
    packageableSupplies, pickedReturnSupplyIds]);

  // Renders a self-contained printable label for a return package.
  const createReturnPackage = useCallback(async (storeId) => {
    const orderIds = returningOrders
      .filter((o) => (o.store?._id || o.store) === storeId && pickedReturnIds[o._id])
      .map((o) => o._id);
    const transferRequestIds = packageableLensTransfers
      .filter((r) => (r.requestingStore?._id || r.requestingStore) === storeId && pickedReturnTransferIds[r._id])
      .map((r) => r._id);
    const pickedSupplies = packageableSupplies
      .filter((sup) => sup.destinationStoreId === storeId && pickedReturnSupplyIds[sup._id]);
    const supplyRequestIds = pickedSupplies.map((sup) => sup._id);
    if (!orderIds.length && !transferRequestIds.length && !supplyRequestIds.length) {
      setReturnNotice({ kind: 'error', message: 'Select at least one order, lens transfer or accessory to bundle.' });
      return;
    }
    setReturnBusy(true);
    setReturnNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        '/api/packages/return',
        { orderIds, transferRequestIds, supplyRequestIds, destinationStoreId: storeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pkg = resp.data?.package;
      const counts = resp.data?.counts || {};
      const parts = [
        counts.orders ? `${counts.orders} order(s)` : null,
        counts.transfers ? `${counts.transfers} lens transfer(s)` : null,
        counts.supplies ? `${counts.supplies} accessory/accessories` : null,
      ].filter(Boolean).join(', ');
      // `warning` means accessories were picked but the join table is not there
      // yet (migration 018). Say so plainly rather than let someone tape up a
      // box believing the accessories were recorded in it.
      setReturnNotice({
        kind: resp.data?.warning ? 'error' : 'success',
        message: resp.data?.warning
          ? `Package ${pkg?.packageNumber} created with ${parts || 'nothing'} — but ${resp.data.warning}`
          : `Return package ${pkg?.packageNumber} created for ${pkg?.originStore?.name || 'the store'} — ${parts}.`,
        package: pkg,
      });
      setPickedReturnIds({});
      setPickedReturnTransferIds({});
      setPickedReturnSupplyIds({});
      if (pkg) printTransferPackageLabel(pkg, { supplies: resp.data?.warning ? [] : pickedSupplies });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setReturnNotice({
        kind: 'error',
        message: err.response?.data?.message || 'Failed to create return package.',
      });
    } finally {
      setReturnBusy(false);
    }
  }, [returningOrders, packageableLensTransfers, pickedReturnIds, pickedReturnTransferIds,
    packageableSupplies, pickedReturnSupplyIds, printTransferPackageLabel]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const updateLensTask = async (order, action) => {
    if (!order?._id || !action) return;
    setLensTaskBusyId(`${order._id}:${action}`);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `/api/orders/${order._id}/lens-fulfillment/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = resp.data?.order;
      if (updated?._id) {
        setLensTasks((prev) => prev.map((item) => item._id === updated._id ? updated : item));
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update lens task.');
    } finally {
      setLensTaskBusyId(null);
    }
  };

  const markSupplierOrderOrdered = async (row) => {
    const orderIds = row?.orderIds?.length ? row.orderIds : row?.orderId ? [row.orderId] : [];
    if (!orderIds.length) return;
    setSupplierOrderBusyId(orderIds.join(':'));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // A row in this list can be a customer order, a store's stock request, or
      // central's own refill. The last two are stock_transfer_requests carrying
      // a synthetic STR- id, so posting them to the orders endpoint just said
      // "Order not found".
      await Promise.all(orderIds.map((id) => {
        const raw = String(id);
        if (raw.startsWith('STR-')) {
          return axios.post(
            `/api/stock-transfer-requests/${raw.slice(4)}/status`,
            { status: 'supplier_ordered' },
            { headers }
          );
        }
        return axios.post(
          `/api/orders/${raw}/lens-fulfillment/ordered`,
          { lensName: row?.lensName || null },
          { headers }
        );
      }));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to mark lens ordered.');
    } finally {
      setSupplierOrderBusyId(null);
    }
  };

  const uniqueJoined = (values, fallback = '—') => {
    const unique = [...new Set((values || []).map((value) => String(value || '').trim()).filter(Boolean))];
    return unique.length ? unique.join(', ') : fallback;
  };

  const printSupplierLensBatch = (batch) => {
    const rows = batch?.rows || [];
    const htmlRows = rows.map((row) => `
      <tr>
        <td>${row.lensName || ''}${(row.quantity || 1) > 1 ? ` ×${row.quantity}` : ''}</td>
        <td>${row.displayCorrection || ''}</td>
        <td>${row.orderNumbers?.join(', ') || row.orderNumber || ''}</td>
        <td>${uniqueJoined(row.orders?.map((order) => order.store?.name), row.store?.name || '')}</td>
        <td>${uniqueJoined(row.orders?.map((order) => order.salesPerson ? `${order.salesPerson.firstName || ''} ${order.salesPerson.lastName || ''}`.trim() : ''), row.salesPerson ? `${row.salesPerson.firstName || ''} ${row.salesPerson.lastName || ''}`.trim() : '')}</td>
        <td>${uniqueJoined(row.orders?.map((order) => order.visitor ? `${order.visitor.firstName || ''} ${order.visitor.lastName || ''}`.trim() : ''), row.visitor ? `${row.visitor.firstName || ''} ${row.visitor.lastName || ''}`.trim() : '')}</td>
      </tr>
    `).join('');
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>${batch.label}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            h1 { margin: 0 0 4px; font-size: 22px; }
            .meta { color: #64748b; margin-bottom: 18px; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { text-align: left; background: #f1f5f9; color: #334155; }
            th, td { padding: 9px 8px; border: 1px solid #cbd5e1; vertical-align: top; }
            small { color: #64748b; }
          </style>
        </head>
        <body>
          <h1>${batch.label}</h1>
          <div class="meta">Due: ${formatDateTime(batch.dueAt)} · ${batch.lensCount || 0} lenses / ${batch.orderCount || 0} orders</div>
          <table>
            <thead>
              <tr><th>Lens Name</th><th>Correction</th><th>Order #</th><th>Store</th><th>Sales Person</th><th>Client</th></tr>
            </thead>
            <tbody>${htmlRows || '<tr><td colspan="6">Nothing to order in this batch.</td></tr>'}</tbody>
          </table>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  // The grid on paper. Same 49×17 sheet as the screen and the printed stock
  // sheet staff already navigate by position, so the empty cells and the colour
  // banding are kept rather than compacted away — a grid with the blanks
  // squeezed out is a different document and can't be read at a glance.
  // Landscape, because 17 CYL columns plus the SPH spine will not sit on
  // portrait at a legible size.
  const printSupplierLensGrid = (batch) => {
    const groups = buildSupplierLensMatrix(batch?.rows, 'both');
    const win = window.open('', '_blank', 'width=1100,height=760');
    if (!win) return;

    const gridHtml = groups.map((group) => {
      const head = group.cylValues.map((cyl) => `<th>${formatSignedCyl(cyl)}</th>`).join('');
      const body = group.sphValues.map((sph) => {
        const cells = group.cylValues.map((cyl) => {
          const qty = group.byCell.get(`${lensKey(sph)}:${lensKey(cyl)}`);
          if (!qty) return `<td style="background:${zoneBg(sph, Math.abs(cyl))}"></td>`;
          return `<td class="has-qty">${qty}</td>`;
        }).join('');
        return `<tr><th class="sph">${Number(sph).toString()}</th>${cells}</tr>`;
      }).join('');

      const unplaced = group.unplaced.length ? `
        <div class="unplaced">
          <strong>Not on the grid — ${group.unplaced.length} to order separately:</strong>
          <ul>${group.unplaced.map((row) => `
            <li>${row.quantity || 1}× ${row.displayCorrection || 'no correction recorded'}
              (${row.orderNumbers?.join(', ') || row.orderNumber || ''})</li>`).join('')}</ul>
        </div>` : '';

      return `
        <section>
          <h2>${group.name} <span class="count">${group.placed} unit(s)</span></h2>
          <table class="grid">
            <thead>
              <tr><th class="corner" rowspan="2">SPH</th><th colspan="${group.cylValues.length}">CYL</th></tr>
              <tr>${head}</tr>
            </thead>
            <tbody>${body}</tbody>
          </table>
          ${unplaced}
        </section>`;
    }).join('');

    win.document.write(`
      <html>
        <head>
          <title>${batch.label} — grid</title>
          <style>
            @page { size: landscape; margin: 10mm; }
            /* Explicit white: without it the sheet inherits the viewer's theme,
               which turns a Save-as-PDF from a dark-mode browser into dark
               paper with unreadable headings. */
            body { font-family: Arial, sans-serif; padding: 0; color: #0f172a; background: #fff; }
            /* Keep the banding when printing — it is the thing staff navigate
               by, and browsers drop backgrounds by default. */
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            h1 { margin: 0 0 4px; font-size: 20px; }
            .meta { color: #64748b; margin-bottom: 14px; font-size: 12px; }
            section { page-break-inside: avoid; margin-bottom: 18px; }
            h2 { font-size: 14px; margin: 0 0 6px; }
            h2 .count { color: #64748b; font-weight: normal; font-size: 12px; }
            table.grid { border-collapse: collapse; }
            table.grid th, table.grid td {
              border: 1px solid #94a3b8; width: 26px; height: 14px;
              font-size: 9px; text-align: center; padding: 0;
            }
            table.grid thead th { background: #fef9c3; font-weight: bold; }
            table.grid th.sph { background: #f1f5f9; font-weight: bold; width: 34px; }
            table.grid td.has-qty { background: #fee2e2; font-weight: bold; font-size: 10px; }
            .unplaced { margin-top: 6px; font-size: 11px; color: #b45309; }
            .unplaced ul { margin: 3px 0 0; padding-left: 16px; }
          </style>
        </head>
        <body>
          <h1>${batch.label} — order grid</h1>
          <div class="meta">
            Due: ${formatDateTime(batch.dueAt)} · ${batch.lensCount || 0} lenses / ${batch.orderCount || 0} orders
          </div>
          ${gridHtml || '<p>Nothing to order in this batch.</p>'}
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  // Incoming orders under one folded header per store (count + total).
  const toggleIncomingStore = (key) => setOpenIncomingStores((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const groupOrdersByStore = (rows) => {
    const groups = new Map();
    rows.forEach((o) => {
      const key = o.store?._id || o.store?.id || o.store?.name || 'none';
      if (!groups.has(key)) groups.set(key, { key, name: o.store?.name || '—', orders: [], total: 0 });
      const g = groups.get(key);
      g.orders.push(o);
      g.total += Number(o.total) || 0;
    });
    return [...groups.values()].sort((a, b) => (b.orders.length - a.orders.length) || a.name.localeCompare(b.name));
  };

  const renderOrdersTable = (rows, emptyMsg) => (
    <Table>
      <thead>
        <tr>
          <th>{t('central.order')}</th>
          <th>{t('central.status')}</th>
          <th>{t('central.salesPerson')}</th>
          <th>{t('central.total')}</th>
          <th>{t('central.updated')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td className="muted" colSpan={5}>{emptyMsg}</td></tr>
        ) : groupOrdersByStore(rows).flatMap((g) => [
          <tr key={`store-${g.key}`} onClick={() => toggleIncomingStore(g.key)} style={{ cursor: 'pointer', background: 'var(--surface-2, #f8fafc)' }}>
            <td colSpan={5} style={{ fontWeight: 700, color: '#0f172a' }}>
              {openIncomingStores.has(g.key) ? <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /> : <ChevronRight size={14} style={{ verticalAlign: '-2px' }} />}
              {' '}{g.name}
              <span style={{ fontWeight: 500, color: '#64748b', marginLeft: '0.6rem', fontSize: '0.8rem' }}>
                {t('central.lensTaskStoreCount', { count: g.orders.length })} · {formatCurrency(g.total)}
              </span>
            </td>
          </tr>,
          ...(!openIncomingStores.has(g.key) ? [] : g.orders.map((o) => {
          const pill = STATUS_PILL[o.status] || { bg: '#e0e7ef', color: '#475569' };
          const statusLabel = pill.labelKey
            ? t(pill.labelKey)
            : String(o.status || '').replace(/_/g, ' ');
          return (
            <tr key={o._id}>
              <td>
                <strong style={{ color: '#0f172a' }}>{o.orderNumber}</strong>
              </td>
              <td><StatusPill bg={pill.bg} color={pill.color}>{statusLabel}</StatusPill></td>
              <td>{o.salesPerson ? `${o.salesPerson.firstName || ''} ${o.salesPerson.lastName || ''}`.trim() : '—'}</td>
              <td>{formatCurrency(o.total)}</td>
              <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{formatDateTime(o.updatedAt || o.orderDate)}</td>
            </tr>
          );
        })),
        ])}
      </tbody>
    </Table>
  );

  const renderReservations = () => (
    <Table>
      <thead>
        <tr>
          <th>{t('central.reservedAt')}</th>
          <th>{t('central.where')}</th>
          <th>{t('central.order')}</th>
          <th>{t('central.lens')}</th>
          <th>{t('central.sph')}</th>
          <th>{t('central.cyl')}</th>
          <th>{t('central.qty')}</th>
          <th>{t('central.eye')}</th>
          <th>{t('central.salesPerson')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {reservations.length === 0 ? (
          <tr><td className="muted" colSpan={10}>{t('central.nothingIsReservedRightNow')}</td></tr>
        ) : reservations.map((r) => (
          <tr key={r._id}>
            <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{formatDateTime(r.createdAt)}</td>
            <td style={{ fontSize: '0.82rem' }}>
              {r.location?.name || '—'}
              {r.location?.isCentralWarehouse && (
                <span style={{ color: '#0f766e', fontWeight: 700 }}> {t('central.central')}</span>
              )}
            </td>
            {/* A hold with no order behind it is the one worth acting on — it is
                stock locked away that nobody is waiting for. */}
            <td>
              {r.order?.orderNumber
                ? <strong style={{ color: '#0f172a' }}>{r.order.orderNumber}</strong>
                : <span style={{ color: '#b45309', fontWeight: 700, fontSize: '0.8rem' }} title={r.reason}>{t('central.noOrder')}</span>}
            </td>
            <td>{r.productName || '—'}</td>
            <td>{r.sph != null ? Number(r.sph).toFixed(2) : '—'}</td>
            <td>{r.cyl != null ? Number(r.cyl).toFixed(2) : '—'}</td>
            <td><strong>{r.qty ?? 1}</strong></td>
            <td>{r.eye || '—'}</td>
            <td>{r.order?.salesPerson ? `${r.order.salesPerson.firstName || ''} ${r.order.salesPerson.lastName || ''}`.trim() : '—'}</td>
            <td>
              <Button
                onClick={() => releaseReservation(r)}
                disabled={!r.releasable || releasingId === r._id}
                title={t('central.putTheseUnitsBackInto')}
              >
                {releasingId === r._id ? t('central.releasing') : t('central.release')}
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  // Lens tasks grouped by the store the order came from: one header per
  // store with its count and how many still need doing, then its orders.
  // Stores with work still to do come first; all start folded, a header click opens one.
  const LENS_TASK_DONE = new Set(['lens_ready_waiting_frame', 'lens_arrived_waiting_frame', 'lens_arrived_equipment_ready']);
  const lensTaskGroups = (() => {
    const groups = new Map();
    lensTasks.forEach((o) => {
      const key = o.store?._id || o.store?.id || o.store?.name || 'none';
      if (!groups.has(key)) groups.set(key, { key, name: o.store?.name || '—', orders: [], todo: 0 });
      const g = groups.get(key);
      g.orders.push(o);
      if (!LENS_TASK_DONE.has(o.lensFulfillment?.status)) g.todo += 1;
    });
    return [...groups.values()].sort((a, b) => (b.todo - a.todo) || (b.orders.length - a.orders.length) || a.name.localeCompare(b.name));
  })();
  const toggleLensStore = (key) => setOpenLensStores((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const renderLensTasks = () => (
    <Table>
      <thead>
        <tr>
          <th>{t('central.order')}</th>
          <th>{t('central.lensStatus')}</th>
          <th>{t('central.salesPerson')}</th>
          <th>{t('central.lens')}</th>
          <th>{t('central.correction')}</th>
          <th>{t('central.updated')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {lensTasks.length === 0 ? (
          <tr><td className="muted" colSpan={7}>{t('central.noLensTasksWaitingFor')}</td></tr>
        ) : lensTaskGroups.flatMap((g) => [
          <tr key={`store-${g.key}`} onClick={() => toggleLensStore(g.key)} style={{ cursor: 'pointer', background: 'var(--surface-2, #f8fafc)' }}>
            <td colSpan={7} style={{ fontWeight: 700, color: '#0f172a' }}>
              {!openLensStores.has(g.key) ? <ChevronRight size={14} style={{ verticalAlign: '-2px' }} /> : <ChevronDown size={14} style={{ verticalAlign: '-2px' }} />}
              {' '}{g.name}
              <span style={{ fontWeight: 500, color: '#64748b', marginLeft: '0.6rem', fontSize: '0.8rem' }}>
                {t('central.lensTaskStoreCount', { count: g.orders.length })}
                {g.todo > 0 && <> · <span style={{ color: '#b91c1c', fontWeight: 700 }}>{t('central.lensTaskToDo', { count: g.todo })}</span></>}
              </span>
            </td>
          </tr>,
          ...(!openLensStores.has(g.key) ? [] : g.orders.map((o) => {
          const lf = o.lensFulfillment || {};
          const pill = LENS_FULFILLMENT_PILL[lf.status] || { bg: '#e0e7ef', color: '#475569' };
          const lensStatusLabel = pill.labelKey
            ? t(pill.labelKey)
            : (lf.status ? String(lf.status).replace(/_/g, ' ') : t('central.unknown'));
          const lensItem = Array.isArray(o.items) ? o.items.find((item) => item.type === 'lens') : null;
          const busyReady = lensTaskBusyId === `${o._id}:ready`;
          const busyOrdered = lensTaskBusyId === `${o._id}:ordered`;
          const busyArrived = lensTaskBusyId === `${o._id}:arrived`;
          return (
            <tr key={o._id}>
              <td><strong style={{ color: '#0f172a' }}>{o.orderNumber}</strong></td>
              <td>
                <StatusPill bg={pill.bg} color={pill.color}>{lensStatusLabel}</StatusPill>
                {/* The store's package (with the frame) is already at central
                    while the lens isn't: the lens is now what holds it up. */}
                {o.frameArrival && !LENS_TASK_DONE.has(lf.status) && (
                  <div style={{ marginTop: 4 }}>
                    <StatusPill bg="#ffedd5" color="#9a3412" title={o.frameArrival.packageNumber || ''}>{t('central.frameArrivedWaitingLens')}</StatusPill>
                  </div>
                )}
              </td>
              <td>{o.salesPerson ? `${o.salesPerson.firstName || ''} ${o.salesPerson.lastName || ''}`.trim() : '—'}</td>
              <td>{lensItem?.name || t('central.prescriptionLens')}</td>
              <td style={{ fontSize: '0.8rem', color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                {(o.correction || []).length === 0 ? '—' : o.correction.map((c) => (
                  <div key={c.visionPair || 'rx'} style={{ marginBottom: o.correction.length > 1 ? 4 : 0 }}>
                    {c.visionPair && (
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#4338ca' }}>
                        {t(c.visionPair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar')}
                      </div>
                    )}
                    {c.od && <div><strong>OD</strong> {c.od}</div>}
                    {c.os && <div><strong>OS</strong> {c.os}</div>}
                  </div>
                ))}
              </td>
              <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{formatDateTime(o.updatedAt || o.orderDate)}</td>
              <td>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {lf.status === 'reserved_waiting_inventory' && (
                    <Button className="primary" disabled={busyReady} onClick={() => updateLensTask(o, 'ready')}>
                      <CheckCircle2 size={13} /> {busyReady ? t('central.saving') : t('central.lensReadyWaitingForFrame')}
                    </Button>
                  )}
                  {lf.status === 'supplier_order_required' && (
                    <Button disabled={busyOrdered} onClick={() => updateLensTask(o, 'ordered')}>
                      <ShoppingCart size={13} /> {busyOrdered ? t('central.saving') : t('central.lensOrderedWaitingForSupplier')}
                    </Button>
                  )}
                  {lf.status === 'lens_ordered_waiting_supplier' && (
                    <Button className="primary" disabled={busyArrived} onClick={() => updateLensTask(o, 'arrived')}>
                      <CheckCircle2 size={13} /> {busyArrived ? t('central.saving') : t('central.lensArrivedWaitingForFrame')}
                    </Button>
                  )}
                  {['lens_ready_waiting_frame', 'lens_arrived_waiting_frame'].includes(lf.status) && (o.frameArrival
                    ? <span style={{ color: '#166534', fontWeight: 700, fontSize: '0.8rem' }}>{t('central.frameAndLensAtCentral')}</span>
                    : <span style={{ color: '#92400e', fontWeight: 700, fontSize: '0.8rem' }}>{t('central.waitingForFramePackageAt')}</span>
                  )}
                  {lf.status === 'lens_arrived_equipment_ready' && (
                    <span style={{ color: '#166534', fontWeight: 700, fontSize: '0.8rem' }}>{t('central.equipmentReadyForLabHandoff')}</span>
                  )}
                </div>
              </td>
            </tr>
          );
        })),
        ])}
      </tbody>
    </Table>
  );

  // Two readings of one batch. The list answers "who is waiting"; the grid
  // answers "what do I buy" — which is the question you actually have with the
  // supplier on the phone. Grouped per lens because that is how a supplier
  // order is placed.
  // The supplier grid is drawn in one cylinder notation at a time — minus
  // (0 … -4) or plus (0 … +4), picked with the CYL −/+ switch. A prescription
  // written in the other notation is transposed onto this one (SPH + CYL, CYL
  // flipped), the same optical lens, so every order has a cell in either view.
  const toGridPower = (sph, cyl, sign = 'minus') => {
    if (sph == null && cyl == null) return null;
    let s = Number(sph ?? 0);
    let c = Number(cyl ?? 0);
    if (!Number.isFinite(s) || !Number.isFinite(c)) return null;
    // 'both': the grid has minus and plus columns, so nothing is transposed.
    if ((sign === 'minus' && c > 0) || (sign === 'plus' && c < 0)) {
      s += c;
      c = -c;
    }
    return { sph: Number(s.toFixed(2)), cyl: Number(c.toFixed(2)) };
  };
  const formatSignedCyl = (value) => {
    const n = Number(value);
    if (n === 0) return '0';
    return n > 0 ? `+${n}` : String(n);
  };

  // The printed range, widened only as far as this lens's orders need: a -8.00
  // adds rows down to -8.00 (in quarter steps, so the spine stays readable)
  // instead of being pushed off the grid into a list.
  const extendAxis = (base, values, descending) => {
    const all = new Set(base.map(Number));
    const lo = Math.min(...base), hi = Math.max(...base);
    for (const v of values) {
      all.add(v);
      for (let x = hi + 0.25; x <= v + 1e-9; x += 0.25) all.add(Number(x.toFixed(2)));
      for (let x = lo - 0.25; x >= v - 1e-9; x -= 0.25) all.add(Number(x.toFixed(2)));
    }
    return [...all].sort((a, b) => (descending ? b - a : a - b));
  };

  const buildSupplierLensMatrix = (rows, sign = 'minus') => {
    const groups = new Map();
    for (const row of rows || []) {
      const name = row.lensName || 'Lens';
      const group = groups.get(name) || { name, byCell: new Map(), placed: 0, unplaced: [], sphSeen: [], cylSeen: [] };
      const qty = Number(row.quantity) || 1;
      const power = toGridPower(row.sph, row.cyl, sign);
      // Only a row with no correction recorded at all has nowhere to sit. It is
      // listed under the grid rather than dropped — a lens missing from a
      // supplier order is a customer left waiting.
      if (power) {
        const key = `${lensKey(power.sph)}:${lensKey(power.cyl)}`;
        group.byCell.set(key, (group.byCell.get(key) || 0) + qty);
        group.placed += qty;
        group.sphSeen.push(power.sph);
        group.cylSeen.push(power.cyl);
      } else {
        group.unplaced.push(row);
      }
      groups.set(name, group);
    }
    return [...groups.values()]
      .map((group) => ({
        ...group,
        sphValues: extendAxis(LENS_SPH_VALUES, group.sphSeen, true),
        cylValues: sign === 'both'
          // -4 … 0 … +4: each order sits at the cylinder it was written with.
          ? extendAxis([...new Set([...LENS_CYL_VALUES.map((v) => -v), ...LENS_CYL_VALUES])], group.cylSeen, false)
          : sign === 'plus'
            ? extendAxis(LENS_CYL_VALUES, group.cylSeen, false)
            : extendAxis(LENS_CYL_VALUES.map((v) => (v === 0 ? 0 : -v)), group.cylSeen, true),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const renderSupplierLensMatrix = (batch) => {
    const groups = buildSupplierLensMatrix(batch.rows, 'both');
    if (!groups.length) return <div className="muted" style={{ padding: '1rem' }}>{t('central.nothingToOrderInThis')}</div>;
    return (
      <ReqGrid style={{ padding: '0.9rem' }}>
        {groups.map((group) => {
          // Folded at first, like the line view; both views share what's open.
          const groupKey = `${batch.id}|${group.name}`;
          const open = openOrderLensGroups.has(groupKey);
          return (
          <ReqGroup key={group.name}>
            <ReqGroupHead onClick={() => toggleOrderLensGroup(groupKey)} style={{ cursor: 'pointer' }}>
              <div>
                <div style={{ fontWeight: 800, color: '#0f172a' }}>
                  {open ? <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /> : <ChevronRight size={14} style={{ verticalAlign: '-2px' }} />}
                  {' '}{group.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {group.placed} {t('central.unitSToOrder')}
                  {group.unplaced.length > 0 && (
                    <span style={{ color: '#b45309', fontWeight: 700 }}>
                      {' '}· {group.unplaced.length} {t('central.notOnTheGrid')}
                    </span>
                  )}
                </div>
              </div>
            </ReqGroupHead>

            {open && (<>
            <ReqTableWrap>
              <ReqTable>
                <thead>
                  <tr><th colSpan={group.cylValues.length + 1}>{group.name}</th></tr>
                  <tr>
                    <th rowSpan={2}>{t('central.sph')}</th>
                    <th colSpan={group.cylValues.length}>{t('central.cyl')}</th>
                  </tr>
                  <tr>
                    {group.cylValues.map((cyl) => <th key={cyl}>{formatSignedCyl(cyl)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {group.sphValues.map((sph) => (
                    <tr key={sph}>
                      <ReqSphHeader $value={Number(sph)}>{Number(sph).toString()}</ReqSphHeader>
                      {group.cylValues.map((cyl) => {
                        const qty = group.byCell.get(`${lensKey(sph)}:${lensKey(cyl)}`);
                        if (!qty) return <td key={cyl}><ReqEmptyCell $bg={zoneBg(sph, Math.abs(cyl))} /></td>;
                        return (
                          <td key={cyl}>
                            {/* Read-only here on purpose: marking an order sent
                                belongs to the line view, where you can see who
                                each lens is for before committing. */}
                            <ReqCell
                              as="div"
                              $bg="#fee2e2"
                              $color="#991b1b"
                              $selectable={false}
                              title={`SPH ${sph} / CYL ${formatSignedCyl(cyl)} — ${qty} to order`}
                            >
                              {qty}
                            </ReqCell>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </ReqTable>
            </ReqTableWrap>

            {group.unplaced.length > 0 && (
              <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: '#b45309' }}>
                {t('central.notShownOnTheGrid')}
                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {group.unplaced.map((row, idx) => (
                    <li key={`${row.orderNumber}:${idx}`}>
                      {row.quantity || 1}× {row.displayCorrection} ({row.orderNumbers?.join(', ') || row.orderNumber})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </>)}
          </ReqGroup>
          );
        })}
      </ReqGrid>
    );
  };

  const renderSupplierLensOrders = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {supplierLensBatches.length === 0 ? (
        <div className="muted">{t('central.noSupplierLensOrderLists')}</div>
      ) : supplierLensBatches.map((batch) => (
        <div key={batch.id} style={{ border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '0.9rem 1rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <strong style={{ color: '#0f172a' }}>{batch.label}</strong>
              <div style={{ color: '#64748b', fontSize: '0.82rem' }}>{t('central.due')} {formatDateTime(batch.dueAt)}</div>
              <div style={{ color: '#b45309', fontSize: '0.78rem', fontWeight: 700 }}>
                {t('central.reminder')} {formatDateTime(batch.reminderAt)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                type="button"
                onClick={() => setSupplierLensView(supplierLensView === 'matrix' ? 'list' : 'matrix')}
                title={supplierLensView === 'matrix'
                  ? 'Show one line per lens, with the order, store and client it is for'
                  : 'Show the powers as an SPH/CYL grid, the same sheet as Lens Stock'}
              >
                {supplierLensView === 'matrix' ? <><ListChecks size={14} /> {t('central.lineView')}</> : <><LayoutGrid size={14} /> {t('central.gridView')}</>}
              </Button>
              <Button type="button" disabled={!batch.rows?.length} onClick={() => printSupplierLensBatch(batch)}>
                <Printer size={14} /> {t('central.printList')}
              </Button>
              {/* Both are offered whichever view is open: you may want the grid
                  on paper while reading the list on screen, or the reverse. */}
              <Button type="button" disabled={!batch.rows?.length} onClick={() => printSupplierLensGrid(batch)}>
                <Printer size={14} /> {t('central.printGrid')}
              </Button>
              <StatusPill bg={batch.rows?.length ? '#fee2e2' : '#dcfce7'} color={batch.rows?.length ? '#991b1b' : '#166534'}>
                {batch.lensCount || 0} {t('central.lenses')} {batch.orderCount || 0} orders
              </StatusPill>
            </div>
          </div>
          {supplierLensView === 'matrix' ? renderSupplierLensMatrix(batch) : (
          <Table>
            <thead>
              <tr>
                <th>{t('central.lensName')}</th>
                <th>{t('central.correction')}</th>
                <th>{t('central.order')}</th>
                <th>{t('central.store')}</th>
                <th>{t('central.salesPerson')}</th>
                <th>{t('central.client')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!batch.rows?.length ? (
                <tr><td className="muted" colSpan={7}>{t('central.nothingToOrderInThis')}</td></tr>
              ) : groupRowsByLens(batch.rows).flatMap((g) => {
                // One folded header per product; its corrections open below.
                const groupKey = `${batch.id}|${g.lensName}`;
                const open = openOrderLensGroups.has(groupKey);
                return [
                  <tr key={`lens-${groupKey}`} onClick={() => toggleOrderLensGroup(groupKey)} style={{ cursor: 'pointer', background: 'var(--surface-2, #f8fafc)' }}>
                    <td colSpan={7} style={{ fontWeight: 700, color: '#0f172a' }}>
                      {open ? <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /> : <ChevronRight size={14} style={{ verticalAlign: '-2px' }} />}
                      {' '}{g.lensName}
                      <span style={{ fontWeight: 500, color: '#64748b', marginLeft: '0.6rem', fontSize: '0.8rem' }}>
                        {t('central.lensGroupLenses', { count: g.lenses })} · {t('central.lensGroupCorrections', { count: g.rows.length })}
                      </span>
                    </td>
                  </tr>,
                  ...(!open ? [] : g.rows.map((row, idx) => {
                const busyKey = row.orderIds?.length ? row.orderIds.join(':') : String(row.orderId);
                return (
                <tr key={`${row.lensName}:${row.displayCorrection}:${idx}`}>
                  <td>
                    <strong style={{ color: '#0f172a' }}>{row.lensName}</strong>
                    {(row.quantity || 1) > 1 && (
                      <StatusPill bg="#dbeafe" color="#1e40af" style={{ marginLeft: 8 }}>×{row.quantity}</StatusPill>
                    )}
                    {/* Not a customer waiting — central buying back what it
                        sold or sent out, so the shelf level holds. A group can
                        mix both, so say how many when it does. */}
                    {(row.isRefill || row.refillQty > 0) && (
                      <StatusPill bg="#f0fdf4" color="#166534" style={{ marginLeft: 8 }}>
                        {row.isRefill ? t('central.refillStock') : `${row.refillQty} refill`}
                      </StatusPill>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#334155' }}>{row.displayCorrection}</div>
                  </td>
                  <td>{row.orderNumbers?.join(', ') || row.orderNumber}</td>
                  <td>{uniqueJoined(row.orders?.map((order) => order.store?.name), row.store?.name || '—')}</td>
                  <td>{uniqueJoined(row.orders?.map((order) => order.salesPerson ? `${order.salesPerson.firstName || ''} ${order.salesPerson.lastName || ''}`.trim() : ''), row.salesPerson ? `${row.salesPerson.firstName || ''} ${row.salesPerson.lastName || ''}`.trim() : '—')}</td>
                  <td>{uniqueJoined(row.orders?.map((order) => order.visitor ? `${order.visitor.firstName || ''} ${order.visitor.lastName || ''}`.trim() : ''), row.visitor ? `${row.visitor.firstName || ''} ${row.visitor.lastName || ''}`.trim() : '—')}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      type="button"
                      className="primary"
                      disabled={supplierOrderBusyId === busyKey}
                      onClick={() => markSupplierOrderOrdered(row)}
                    >
                      <CheckCircle2 size={13} /> {supplierOrderBusyId === busyKey ? t('central.saving') : t('central.ordered')}
                    </Button>
                  </td>
                </tr>
                );
              })),
                ];
              })}
            </tbody>
          </Table>
          )}
        </div>
      ))}
    </div>
  );

  // Lens Order Lists rows (one per lens + correction) under their product.
  const groupRowsByLens = (rows) => {
    const groups = new Map();
    (rows || []).forEach((row) => {
      const name = row.lensName || '—';
      if (!groups.has(name)) groups.set(name, { lensName: name, rows: [], lenses: 0 });
      const g = groups.get(name);
      g.rows.push(row);
      g.lenses += Number(row.quantity) || 1;
    });
    return [...groups.values()].sort((a, b) => (b.lenses - a.lenses) || a.lensName.localeCompare(b.lensName));
  };
  const toggleOrderLensGroup = (key) => setOpenOrderLensGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ── Stock movements: Excel download instead of an on-page list ───────
  const downloadMovements = async () => {
    setMovesDownloading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - movesDays * 864e5);
      const { data } = await axios.get('/api/packages/central/stock-movements-export', {
        params: { from: from.toISOString(), to: to.toISOString() },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // Loaded on click only: the spreadsheet library is large.
      const XLSX = await import('xlsx');
      const power = (v) => (v == null || v === '' ? '' : Number(v));
      const sheetRows = (data?.movements || []).map((m) => ({
        [t('central.xlsDate')]: new Date(m.at).toLocaleString(),
        [t('central.colLocation')]: m.location,
        [t('central.xlsType')]: t(TYPE_KEYS[m.productType] || 'central.mvSupply'),
        [t('central.product')]: m.product,
        SPH: power(m.sph),
        CYL: power(m.cyl),
        [t('central.xlsQty')]: Number(m.qty) || 0,
        [t('central.xlsMovement')]: t(MOVE_KEYS[m.movementType] || 'central.mvAdjust'),
        [t('central.xlsReason')]: m.reason,
        [t('central.order')]: m.order,
        [t('central.eye')]: m.eye,
        [t('central.xlsBy')]: m.by,
      }));
      const sheet = XLSX.utils.json_to_sheet(sheetRows);
      sheet['!cols'] = [20, 16, 10, 26, 7, 7, 7, 14, 16, 24, 6, 18].map((wch) => ({ wch }));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Movements');
      const day = (d) => d.toISOString().slice(0, 10);
      XLSX.writeFile(book, `stock-movements_${day(from)}_${day(to)}.xlsx`);
    } catch (err) {
      window.alert(err.response?.data?.message || t('central.xlsFailed'));
    } finally {
      setMovesDownloading(false);
    }
  };

  // ── Stock movements: one store's shelf next to its requests ──────────
  const loadStoreStock = async (storeId, name) => {
    setStockStore({ storeId, name, loading: true, stock: null, requests: [], error: null });
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const [stockResp, reqResp] = await Promise.all([
        axios.get(`/api/packages/central/store-stock/${storeId}`, { headers }),
        axios.get(`/api/stock-transfer-requests?fromStore=${encodeURIComponent(storeId)}`, { headers }),
      ]);
      setStockStore({ storeId, name, loading: false, stock: stockResp.data, requests: reqResp.data?.requests || [], error: null });
    } catch (err) {
      setStockStore({ storeId, name, loading: false, stock: null, requests: [], error: err.response?.data?.message || err.message });
    }
  };
  const toggleStockStore = (l) => {
    setStockNotice(null);
    if (stockStore?.storeId === l.storeId) { setStockStore(null); return; }
    setOpenStockProducts(new Set());
    loadStoreStock(l.storeId, l.name);
  };
  const toggleStockProduct = (key) => setOpenStockProducts((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Requests split the way central acts on them: ready (central has the lens,
  // not in a box yet), to order (waiting on the supplier), already in a box.
  const requestState = (r) => (r.package ? 'boxed' : READY_STATUSES.includes(r.status) ? 'ready' : 'toOrder');
  const nameKey = (v) => String(v || '').trim().toLowerCase();

  const storeLensProducts = () => {
    const products = new Map();
    const entry = (name) => {
      const key = nameKey(name) || 'lens';
      if (!products.has(key)) products.set(key, { key, name: name || 'Lens', total: 0, stock: new Map(), ready: new Map(), toOrder: new Map(), readyIds: [], readyQty: 0, toOrderQty: 0, boxedQty: 0, sphSeen: [], cylSeen: [] });
      return products.get(key);
    };
    for (const l of stockStore?.stock?.lenses || []) {
      const e = entry(l.name);
      e.total += l.total;
      for (const c of l.cells) {
        const k = `${lensKey(c.sph)}:${lensKey(c.cyl)}`;
        e.stock.set(k, (e.stock.get(k) || 0) + c.qty);
        e.sphSeen.push(c.sph); e.cylSeen.push(c.cyl);
      }
    }
    for (const r of stockStore?.requests || []) {
      if (r.type && r.type !== 'lens') continue;
      const e = entry(r.productName);
      const qty = Number(r.qty) || 1;
      const state = requestState(r);
      if (state === 'boxed') { e.boxedQty += qty; continue; }
      if (r.sph == null && r.cyl == null) continue;
      const k = `${lensKey(r.sph ?? 0)}:${lensKey(r.cyl ?? 0)}`;
      const bucket = state === 'ready' ? e.ready : e.toOrder;
      bucket.set(k, (bucket.get(k) || 0) + qty);
      if (state === 'ready') { e.readyQty += qty; e.readyIds.push(r._id); } else { e.toOrderQty += qty; }
      e.sphSeen.push(Number(r.sph ?? 0)); e.cylSeen.push(Number(r.cyl ?? 0));
    }
    const bothCyl = [...new Set([...LENS_CYL_VALUES.map((v) => -v), ...LENS_CYL_VALUES])];
    return [...products.values()]
      .map((e) => ({ ...e, sphValues: extendAxis(LENS_SPH_VALUES, e.sphSeen, true), cylValues: extendAxis(bothCyl, e.cylSeen, false) }))
      .sort((a, b) => (b.readyQty + b.toOrderQty - a.readyQty - a.toOrderQty) || (b.total - a.total) || a.name.localeCompare(b.name));
  };

  const transferReady = async (product) => {
    const ids = product.readyIds;
    if (!ids.length || !stockStore) return;
    if (!window.confirm(t('central.confirmTransferReady', { count: product.readyQty, product: product.name, store: stockStore.name }))) return;
    setStockTransferBusy(product.key);
    setStockNotice(null);
    try {
      const resp = await axios.post('/api/packages/return', {
        orderIds: [],
        transferRequestIds: ids,
        supplyRequestIds: [],
        destinationStoreId: stockStore.storeId,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const pkg = resp.data?.package;
      if (pkg) printTransferPackageLabel(pkg);
      setStockNotice({ kind: 'success', message: t('central.boxSent', { number: pkg?.packageNumber || '', store: stockStore.name || '' }) });
      loadStoreStock(stockStore.storeId, stockStore.name);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setStockNotice({ kind: 'error', message: err.response?.data?.message || t('central.boxSendFailed') });
    } finally {
      setStockTransferBusy(null);
    }
  };

  const renderStockGrid = (product, which) => (
    <ReqTableWrap>
      <CompactReqTable>
        <thead>
          <tr>
            <th rowSpan={2}>{t('central.sph')}</th>
            <th colSpan={product.cylValues.length}>{t('central.cyl')}</th>
          </tr>
          <tr>{product.cylValues.map((cyl) => <th key={cyl}>{formatSignedCyl(cyl)}</th>)}</tr>
        </thead>
        <tbody>
          {product.sphValues.map((sph) => (
            <tr key={sph}>
              <ReqSphHeader $value={Number(sph)}>{Number(sph).toString()}</ReqSphHeader>
              {product.cylValues.map((cyl) => {
                const k = `${lensKey(sph)}:${lensKey(cyl)}`;
                if (which === 'stock') {
                  const qty = product.stock.get(k);
                  if (!qty) return <td key={cyl}><ReqEmptyCell $bg={zoneBg(sph, Math.abs(cyl))} /></td>;
                  return <td key={cyl}><ReqCell as="div" $bg="#e0f2fe" $color="#075985" $selectable={false}>{qty}</ReqCell></td>;
                }
                const ready = product.ready.get(k);
                const toOrder = product.toOrder.get(k);
                if (!ready && !toOrder) return <td key={cyl}><ReqEmptyCell $bg={zoneBg(sph, Math.abs(cyl))} /></td>;
                return (
                  <td key={cyl}>
                    <ReqCell
                      as="div"
                      $bg={ready ? '#dcfce7' : '#ffedd5'}
                      $color={ready ? '#166534' : '#9a3412'}
                      $selectable={false}
                      title={[ready && `${ready} ${t('central.readyAtCentral')}`, toOrder && `${toOrder} ${t('central.toOrderLabel')}`].filter(Boolean).join(' · ')}
                    >
                      {(ready || 0) + (toOrder || 0)}
                    </ReqCell>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </CompactReqTable>
    </ReqTableWrap>
  );

  const renderStoreStockPanel = () => {
    if (!stockStore) return null;
    const head = (
      <SectionTitle>
        <Boxes size={18} /> {stockStore.name}
        <span style={{ flex: 1 }} />
        <Button onClick={() => setStockKind('lenses')} className={stockKind === 'lenses' ? 'primary' : undefined}>{t('central.colLenses')}</Button>{' '}
        <Button onClick={() => setStockKind('frames')} className={stockKind === 'frames' ? 'primary' : undefined}>{t('central.colFrames')}</Button>
      </SectionTitle>
    );
    if (stockStore.loading) return <TabPanel>{head}<div style={{ color: 'var(--ink-mute)' }}>{t('central.refreshing')}</div></TabPanel>;
    if (stockStore.error) return <TabPanel>{head}<div style={{ color: 'var(--danger)' }}>{stockStore.error}</div></TabPanel>;

    if (stockKind === 'frames') {
      const asked = new Map();
      for (const r of stockStore.requests) {
        if (r.type !== 'frame') continue;
        const k = nameKey(r.productName);
        asked.set(k, (asked.get(k) || 0) + (Number(r.qty) || 1));
      }
      const frames = stockStore.stock?.frames || [];
      return (
        <TabPanel>
          {head}
          <Table>
            <thead><tr><th>{t('central.product')}</th><th style={{ textAlign: 'right' }}>{t('central.inStore', { store: stockStore.name })}</th><th style={{ textAlign: 'right' }}>{t('central.askedFromCentral')}</th></tr></thead>
            <tbody>
              {frames.length === 0 && asked.size === 0 ? (
                <tr><td className="muted" colSpan={3}>{t('central.nothing')}</td></tr>
              ) : frames.map((f) => (
                <tr key={f.productId}>
                  <td>{f.name}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(f.qty)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{asked.get(nameKey(f.name)) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TabPanel>
      );
    }

    const products = storeLensProducts();
    return (
      <TabPanel>
        {head}
        {stockNotice && <PageNotice $kind={stockNotice.kind}>{stockNotice.message}</PageNotice>}
        {products.length === 0 && <div style={{ color: 'var(--ink-mute)' }}>{t('central.nothing')}</div>}
        {products.map((product) => {
          const key = `${stockStore.storeId}|${product.key}`;
          const open = openStockProducts.has(key);
          return (
            <div key={product.key} style={{ borderTop: '1px solid var(--line)', padding: '0.6rem 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <span onClick={() => toggleStockProduct(key)} style={{ cursor: 'pointer', fontWeight: 700, color: 'var(--ink)' }}>
                  {open ? <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /> : <ChevronRight size={14} style={{ verticalAlign: '-2px' }} />}
                  {' '}{product.name}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
                  {t('central.inStore', { store: stockStore.name })} <b>{fmtNum(product.total)}</b>
                  {' · '}{t('central.askedFromCentral')}{' '}
                  {product.readyQty > 0 && <Tag $tone="ok">{product.readyQty} {t('central.readyAtCentral')}</Tag>}{' '}
                  {product.toOrderQty > 0 && <Tag $tone="warn">{product.toOrderQty} {t('central.toOrderLabel')}</Tag>}
                  {!product.readyQty && !product.toOrderQty && t('central.none')}
                  {product.boxedQty > 0 && <> · {t('central.inABox', { count: product.boxedQty })}</>}
                </span>
                <span style={{ flex: 1 }} />
                {product.readyQty > 0 && (
                  <Button className="primary" disabled={stockTransferBusy === product.key} onClick={() => transferReady(product)}>
                    <Truck size={14} /> {stockTransferBusy === product.key ? t('central.sending') : t('central.transferReadyTo', { count: product.readyQty, store: stockStore.name })}
                  </Button>
                )}
              </div>
              {open && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', marginTop: '0.6rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>{t('central.inStore', { store: stockStore.name })} · {fmtNum(product.total)}</div>
                    {renderStockGrid(product, 'stock')}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                      {t('central.askedFromCentral')} · {fmtNum(product.readyQty + product.toOrderQty)}{' '}
                      <Tag $tone="ok">{t('central.readyAtCentral')}</Tag>{' '}<Tag $tone="warn">{t('central.toOrderLabel')}</Tag>
                    </div>
                    {renderStockGrid(product, 'requests')}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </TabPanel>
    );
  };

  const renderActivityFeed = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
      {activities.length === 0 ? (
        <div className="muted">{t('central.noCentralActivityRecordedYet')}</div>
      ) : activities.map((item) => (
        <div
          key={item.id}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(150px, 0.9fr) minmax(180px, 1fr) minmax(260px, 2fr) minmax(120px, 0.9fr)',
            gap: '0.75rem',
            alignItems: 'center',
            padding: '0.8rem 0.9rem',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            background: '#f8fafc',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.actor || 'System'}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{formatStatusLabel(item.actorRole)}</div>
          </div>
          <div>
            <div style={{ fontWeight: 700, color: '#0369a1' }}>{item.subject || item.packageNumber || item.orderNumber || 'Central action'}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
              {[formatStatusLabel(item.type), item.storeName].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div>
            <div style={{ color: '#334155', fontWeight: 600 }}>{item.action || formatStatusLabel(item.status)}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
              {[formatStatusLabel(item.status), item.location].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'right' }}>
            {formatDateTime(item.at)}
          </div>
        </div>
      ))}
    </div>
  );

  // Counts how many of the inbound packages are still un-opened.
  const inboundUnopenedCount = inboundPackages.filter((p) => !p.unpackedAt).length;

  // Oldest timestamp in a list, so a queue can say how long it has been
  // waiting. Rows without a date are skipped, not treated as "now".
  const oldestOf = (rows, pick) => rows.reduce((min, row) => {
    const ts = new Date(pick(row) || 0).getTime();
    return ts > 0 && (min == null || ts < min) ? ts : min;
  }, null);
  const ageHours = (ts) => (ts == null ? null : Math.max(0, (Date.now() - ts) / 36e5));
  const formatAge = (hours) => {
    if (hours < 1) return t('central.ageJustNow');
    if (hours < 24) return t('central.ageHours', { count: Math.floor(hours) });
    return t('central.ageDays', { count: Math.floor(hours / 24) });
  };
  // Three days is when a customer starts phoning the store; one day is when
  // it stops being "this morning's work".
  const toneForAge = (hours) => {
    if (hours == null) return 'fresh';
    if (hours >= 72) return 'late';
    if (hours >= 24) return 'aging';
    return 'fresh';
  };

  // The supplier queue has no single age — it is cut into batches with a
  // cut-off — so its urgency is the next cut-off, and a missed one is late.
  const nextSupplierBatch = supplierLensBatches
    .filter((b) => (b.lensCount || 0) > 0 && b.dueAt)
    .sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt))[0];
  const supplierBatchDue = nextSupplierBatch ? new Date(nextSupplierBatch.dueAt) : null;
  const supplierBatchLate = supplierBatchDue != null && supplierBatchDue.getTime() < Date.now();
  const formatDue = (d) => (d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }));

  const WORK_QUEUE = [
    {
      // Arrived packages are opened per store on Store packages.
      id: 'unpack', tab: 'store-packages', Icon: PackageOpen,
      title: t('central.queueUnpackTitle'), detail: t('central.queueUnpackDetail'), act: t('central.queueUnpackAct'),
      count: inboundUnopenedCount,
      oldest: oldestOf(inboundPackages.filter((p) => !p.unpackedAt), (p) => p.updatedAt || p.createdAt),
    },
    {
      id: 'supplier', tab: 'supplier-lens-orders', Icon: ShoppingCart,
      title: t('central.queueSupplierTitle'), detail: t('central.kpiSupplierHint', { count: supplierLensTotals.orders || 0 }), act: t('central.queueSupplierAct'),
      count: supplierLensTotals.lenses || 0,
      tone: supplierBatchLate ? 'late' : 'fresh',
      pill: supplierBatchDue
        ? (supplierBatchLate ? t('central.queueBatchOverdue') : t('central.queueBatchDue', { time: formatDue(supplierBatchDue) }))
        : null,
    },
    {
      id: 'lens-tasks', tab: 'lens-tasks', Icon: Glasses,
      title: t('central.queueLensTasksTitle'), detail: t('central.kpiLensTasksHint'), act: t('central.queueLensTasksAct'),
      count: lensTasks.length,
      oldest: oldestOf(lensTasks, (o) => o.created_at || o.orderDate || o.createdAt),
    },
    {
      id: 'incoming', tab: 'incoming', Icon: Inbox,
      title: t('central.queueIncomingTitle'), detail: t('central.kpiIncomingHint'), act: t('central.queueIncomingAct'),
      count: incoming.length,
      oldest: oldestOf(incoming, (o) => o.updatedAt || o.orderDate),
    },
    {
      // Store stock requests open on Stock movements (stock + who asks for what).
      id: 'stock-requests', tab: 'stock-movements', Icon: ListChecks,
      title: t('central.queueStockRequestsTitle'), detail: t('central.queueStockRequestsDetail'), act: t('central.queueStockRequestsAct'),
      count: stockRequests.length,
      oldest: oldestOf(stockRequests, (r) => r.createdAt),
    },
    {
      id: 'supplier-receipt', tab: 'supplier-receipt', Icon: Truck,
      title: t('central.queueSupplierDeliveryTitle'), detail: t('central.queueSupplierDeliveryDetail'), act: t('central.queueSupplierDeliveryAct'),
      count: supplierOutstanding || 0,
    },
  ]
    .filter((q) => q.count > 0 && tabOk(q.tab))
    .map((q) => {
      const hours = ageHours(q.oldest);
      return {
        ...q,
        hours,
        tone: q.tone || toneForAge(hours),
        pill: q.pill || (hours != null ? t('central.queueOldest', { age: formatAge(hours) }) : null),
      };
    })
    // Late before aging before fresh; within a tone, whatever has waited
    // longest; then the bigger pile.
    .sort((a, b) => {
      const rank = { late: 0, aging: 1, fresh: 2 };
      return (rank[a.tone] - rank[b.tone]) || ((b.hours || 0) - (a.hours || 0)) || (b.count - a.count);
    });

  const overviewQueue = (
    <TabPanel>
      <QueueHead>
        <SectionTitle as="h2">
          <ListChecks size={18} /> {t('central.queueHeading')}
        </SectionTitle>
        {WORK_QUEUE.length > 0 && <span className="sub">{t('central.queueSub')}</span>}
      </QueueHead>
      {/* Until the first load lands, an empty queue means "not known yet",
          not "nothing to do" — claiming all clear there would be a lie. */}
      {!lastUpdatedAt ? (
        <div style={{ padding: '1rem', color: 'var(--ink-mute)', fontSize: '0.86rem' }}>{t('central.refreshing')}</div>
      ) : WORK_QUEUE.length === 0 ? (
        <AllClear><CheckCircle2 size={18} /> {t('central.queueAllClear')}</AllClear>
      ) : (
        <QueueList>
          {WORK_QUEUE.map(({ id, tab, Icon, title, detail, act, count, tone, pill }) => (
            <li key={id}>
              <QueueRow
                type="button"
                $tone={tone}
                onClick={() => setActiveTab(tab)}
                aria-label={`${count} ${title}. ${detail}.${pill ? ` ${pill}.` : ''} ${t('central.openTab', { name: tabLabel(tab) })}`}
              >
                <span className="count">{count}</span>
                <span className="title"><Icon size={14} aria-hidden="true" /> {title}</span>
                {pill && <span className="age">{pill}</span>}
                <span className="act" aria-hidden="true">{act} <ArrowRight size={13} /></span>
              </QueueRow>
            </li>
          ))}
        </QueueList>
      )}
    </TabPanel>
  );

  // ── Store packages ────────────────────────────────────────────────────
  // One row per store: what is ready to go to it (orders, lens transfers,
  // accessories — the same three things the Returns tab packs into one box),
  // what it has sent that has not been received, and what it asked for that is
  // not ready yet. Central's own refills are not a store and are left out.
  const centralStoreId = String(user.store?._id || user.store?.id || '');
  const storeRowsById = new Map();
  const storeEntry = (id, name) => {
    const key = String(id || 'unknown');
    if (!storeRowsById.has(key)) {
      storeRowsById.set(key, { storeId: key, name: null, orders: [], transfers: [], supplies: [], incoming: [], outgoing: [], waiting: 0 });
    }
    const e = storeRowsById.get(key);
    if (!e.name && name) e.name = name;
    return e;
  };
  // Every store is listed, even with nothing to send or receive.
  (stockOverview?.locations || []).forEach((l) => {
    if (!l.isCentral) storeEntry(l.storeId, l.name);
  });
  returnsByStore.forEach((g) => {
    const e = storeEntry(g.storeId, g.store?.name);
    e.orders = g.orders;
    e.transfers = g.transfers;
    e.supplies = g.supplies;
  });
  [...storePackagesIn, ...inboundPackages.filter((p) => !p.unpackedAt)].forEach((p) => {
    const origin = p.originStore;
    storeEntry(origin?._id || origin?.id || origin, origin?.name).incoming.push(p);
  });
  // Boxes already made for the store, until the store scans them in. A box's
  // origin_store_id is its destination when it leaves central.
  outgoingPackages.forEach((p) => {
    const dest = p.originStore;
    storeEntry(dest?._id || dest?.id || dest, dest?.name).outgoing.push(p);
  });
  notReadyByStore.forEach((list, id) => {
    storeEntry(id, list[0]?.requestingStore?.name).waiting = list.length;
  });
  const readyCount = (e) => e.orders.length + e.transfers.length + e.supplies.length;
  const storeRows = [...storeRowsById.values()]
    .filter((e) => e.storeId !== centralStoreId && (e.name || readyCount(e) || e.outgoing.length || e.incoming.length || e.waiting))
    .sort((a, b) => (readyCount(b) - readyCount(a)) || (b.outgoing.length - a.outgoing.length)
      || (b.incoming.length - a.incoming.length) || (b.waiting - a.waiting)
      || String(a.name || '').localeCompare(String(b.name || '')));
  const storeReadyTotal = storeRows.reduce((s, e) => s + readyCount(e), 0);
  const storesWithReady = storeRows.filter((e) => readyCount(e) > 0).length;
  const storeIncomingTotal = storeRows.reduce((s, e) => s + e.incoming.length, 0);
  const storeOnRoad = storePackagesIn.filter((p) => p.status === 'in_transit_to_central').length;

  const incomingLabel = (p) => {
    if (p.status === 'in_transit_to_central') return { text: t('central.onRoadStatus'), tone: 'warn' };
    if (p.status === 'at_central') return { text: t('central.atCentralSealed'), tone: 'blue' };
    return { text: t('central.waitingForCourier'), tone: 'grey' };
  };

  const boxStatus = (p, storeName) => (p.status === 'in_transit_to_store'
    ? { text: t('central.boxOnWay', { store: storeName || '' }), tone: 'blue' }
    : { text: t('central.boxWaitingCourier'), tone: 'warn' });
  const boxItemCount = (p) => (p.orders?.length || 0)
    + (p.stockTransferRequests || []).reduce((sum, r) => sum + (Number(r.qty) || 1), 0);

  const renderOutBox = (p, storeName) => {
    const st = boxStatus(p, storeName);
    const open = !!openBoxIds[p._id];
    const made = p.createdAt ? new Date(p.createdAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
    return (
      <OutBox key={p._id}>
        <div className="top">
          <span className="num">{p.packageNumber}</span>
          <Tag $tone={st.tone}>{st.text}</Tag>
          <span className="meta">{t('central.boxMeta', { count: boxItemCount(p), date: made })}</span>
          <span className="grow" />
          <Button onClick={() => setOpenBoxIds((m) => ({ ...m, [p._id]: !m[p._id] }))} aria-expanded={open}>
            {open ? t('central.hideContents') : t('central.boxContents')}
          </Button>
          <Button onClick={() => printTransferPackageLabel(p)}><Printer size={14} />{t('central.printLabel')}</Button>
          {p.status === 'at_central_returned' && (
            <Button disabled={thirdPartyBusyId === p._id} onClick={() => setHandoffTarget(p)} title={t('central.useThisWhenTheInternal')}>
              {thirdPartyBusyId === p._id ? t('central.handing') : t('central.giveOutsideCourier')}
            </Button>
          )}
        </div>
        {open && (
          <div className="contents">
            {(p.orders || []).map((o) => (
              <div className="line" key={o._id || o.id}>
                <span>{o.orderNumber}{o.visitor ? ` · ${[o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ')}` : ''}</span>
                <b>{t('central.groupOrdersOne')}</b>
              </div>
            ))}
            {groupTransfers(p.stockTransferRequests || []).map((g) => (
              <div className="line" key={g.key}>
                <span>{g.productName} · {t('central.sph')} {g.sph ?? '—'} {t('central.cyl2')} {g.cyl ?? '—'}</span>
                <b>×{g.qty}</b>
              </div>
            ))}
            {!(p.orders?.length || p.stockTransferRequests?.length) && <div className="line"><span>{t('central.nothing')}</span></div>}
          </div>
        )}
        {p.status === 'at_central_returned' && <div className="hint">{t('central.courierScanNote', { store: storeName || '' })}</div>}
      </OutBox>
    );
  };

  const isLeftOut = (kind, id) => !!leftOut[`${kind}:${id}`];
  const toggleLeftOut = (kind, id) => setLeftOut((m) => ({ ...m, [`${kind}:${id}`]: !m[`${kind}:${id}`] }));
  // Same lens and power on one line: a refill is dozens of single lenses, and
  // a row each made the box unreadable. Ticking a line ticks every unit on it.
  const groupTransfers = (list) => {
    const byKey = new Map();
    list.forEach((r) => {
      const key = [r.productName || '', r.sph, r.cyl].join('|');
      const g = byKey.get(key) || { key, productName: r.productName, sph: r.sph, cyl: r.cyl, qty: 0, ids: [] };
      g.qty += Number(r.qty) || 1;
      g.ids.push(r._id);
      byKey.set(key, g);
    });
    return [...byKey.values()].sort((a, b) => String(a.productName).localeCompare(String(b.productName)) || (Number(a.sph) - Number(b.sph)) || (Number(a.cyl) - Number(b.cyl)));
  };
  const setGroupLeftOut = (ids, out) => setLeftOut((m) => {
    const next = { ...m };
    ids.forEach((id) => { next[`t:${id}`] = out; });
    return next;
  });
  const pickedFor = (e) => ({
    orders: e.orders.filter((o) => !isLeftOut('o', o._id)),
    transfers: e.transfers.filter((r) => !isLeftOut('t', r._id)),
    supplies: e.supplies.filter((s) => !isLeftOut('s', s._id)),
  });

  // One box for the store with everything ticked, and its label printed at
  // once. Same server route the Returns tab uses; this only skips the picking.
  const sendStoreBox = async (e) => {
    const picked = pickedFor(e);
    const total = picked.orders.length + picked.transfers.length + picked.supplies.length;
    if (!total) return;
    setStoreBusy(e.storeId);
    setStoreNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post('/api/packages/return', {
        orderIds: picked.orders.map((o) => o._id),
        transferRequestIds: picked.transfers.map((r) => r._id),
        supplyRequestIds: picked.supplies.map((s) => s._id),
        destinationStoreId: e.storeId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const pkg = resp.data?.package;
      // The branded label, same as Outgoing and the stores print.
      if (pkg) printTransferPackageLabel(pkg, { supplies: resp.data?.warning ? [] : picked.supplies });
      setStoreNotice(resp.data?.warning
        ? { kind: 'error', message: `${pkg?.packageNumber || ''}: ${resp.data.warning}` }
        : { kind: 'success', message: t('central.boxSent', { number: pkg?.packageNumber || '', store: e.name || '' }) });
      setLeftOut({});
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setStoreNotice({ kind: 'error', message: err.response?.data?.message || t('central.boxSendFailed') });
    } finally {
      setStoreBusy(null);
    }
  };

  // Receive (if it is still on the road), unpack, and open the package so the
  // orders inside can be handed to the lab from the usual detail view.
  const receiveStorePackage = async (p) => {
    setStoreBusy(p._id);
    setStoreNotice(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (p.status === 'in_transit_to_central') {
        await axios.post('/api/packages/scan', { code: p.qrCode || p.packageNumber }, { headers });
      }
      await axios.post(`/api/packages/${p._id}/unpack`, {}, { headers });
      setRefreshKey((k) => k + 1);
      openPackageDetail(p);
    } catch (err) {
      setStoreNotice({ kind: 'error', message: err.response?.data?.message || t('central.receiveFailed') });
    } finally {
      setStoreBusy(null);
    }
  };

  // ── Supplier packages ─────────────────────────────────────────────────
  const supplierLinesByAge = [...supplierLines]
    .map((l) => ({ ...l, hours: ageHours(l.createdAt ? new Date(l.createdAt).getTime() : null) }))
    .sort((a, b) => (b.hours || 0) - (a.hours || 0));
  const supplierOldestHours = supplierLinesByAge[0]?.hours ?? null;

  // Marks every line of one batch ordered with the supplier, the same calls the
  // per-row button in Lens Order Lists makes, a few at a time.
  const markBatchOrdered = async (batch) => {
    const rows = batch?.rows || [];
    if (!rows.length) return;
    if (!window.confirm(t('central.confirmBatchOrdered', { count: batch.lensCount || rows.length, label: batch.label || '' }))) return;
    setBatchBusy(true);
    setBatchNotice(null);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    // The list has a row per lens (OD and OS), so an order shows up twice:
    // mark each order or request once. It used to be marked once per row.
    const lensNameById = new Map();
    rows.forEach((row) => {
      const ids = row?.orderIds?.length ? row.orderIds : row?.orderId ? [row.orderId] : [];
      ids.forEach((id) => { if (!lensNameById.has(String(id))) lensNameById.set(String(id), row?.lensName || null); });
    });
    const jobs = [...lensNameById].map(([raw, lensName]) => () => (raw.startsWith('STR-')
      ? axios.post(`/api/stock-transfer-requests/${raw.slice(4)}/status`, { status: 'supplier_ordered' }, { headers })
      : axios.post(`/api/orders/${raw}/lens-fulfillment/ordered`, { lensName }, { headers })));
    // Twelve at a time, each starting as soon as one finishes.
    const results = new Array(jobs.length);
    let next = 0;
    const worker = async () => {
      while (next < jobs.length) {
        const i = next++;
        // eslint-disable-next-line no-await-in-loop
        [results[i]] = await Promise.allSettled([jobs[i]()]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(12, jobs.length) }, worker));
    const failed = results.filter((r) => r.status === 'rejected').length;
    const done = results.length - failed;
    setBatchBusy(false);
    setBatchNotice(failed
      ? { kind: 'error', message: t('central.batchOrderedPartial', { done, failed }) }
      : { kind: 'success', message: t('central.batchOrderedDone', { count: done }) });
    setRefreshKey((k) => k + 1);
  };

  // ── Stock movements ───────────────────────────────────────────────────
  const stockLocations = stockOverview?.locations || [];
  const stockMoves = stockOverview?.movements || [];
  const centralStock = stockLocations.find((l) => l.isCentral) || null;
  const storeRequestTotal = stockLocations.filter((l) => !l.isCentral).reduce((s, l) => s + l.requests + l.refill, 0);
  const MOVE_KEYS = { sale: 'central.mvSale', reserve: 'central.mvReserve', release: 'central.mvRelease', transfer: 'central.mvTransfer', adjust: 'central.mvAdjust', revert: 'central.mvRevert' };
  const TYPE_KEYS = { LENS: 'central.mvLens', FRAME: 'central.mvFrame', SUPPLY: 'central.mvSupply' };
  const fmtNum = (n) => new Intl.NumberFormat().format(n || 0);
  const fmtPower = (m) => (m.sph != null ? ` ${Number(m.sph) > 0 ? '+' : ''}${Number(m.sph).toFixed(2)}${m.cyl ? ` / ${Number(m.cyl).toFixed(2)}` : ''}` : '');
  const moveLine = (m) => `${t(MOVE_KEYS[m.movementType] || 'central.mvAdjust')} · ${m.productName || t(TYPE_KEYS[m.productType] || 'central.mvSupply')}${fmtPower(m)}`;
  const moveTime = (at) => {
    const d = new Date(at);
    return d.toDateString() === new Date().toDateString()
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  };
  // ── Overview cards ────────────────────────────────────────────────────
  const doorHead = (Icon, title) => (
    <div className="head">
      <Icon size={16} aria-hidden="true" /> {title}
      <span className="open">{t('central.cardOpen')} <ArrowRight size={13} /></span>
    </div>
  );

  const overviewDoors = (
    <DoorGrid>
      {tabOk('store-packages') && (
        <DoorCard type="button" onClick={() => setActiveTab('store-packages')} aria-label={`${tabLabel('store-packages')}: ${storeReadyTotal} ${t('central.itemsReadyToSend')}. ${t('central.openTab', { name: tabLabel('store-packages') })}`}>
          {doorHead(Warehouse, tabLabel('store-packages'))}
          <div className="big"><strong>{storeReadyTotal}</strong><span>{t('central.itemsReadyToSend')}</span></div>
          <div className="mini">
            <div><b>{storesWithReady}</b><span>{t('central.storesWaiting')}</span></div>
            <div><b>{storeIncomingTotal}</b><span>{t('central.comingIn')}</span></div>
            <div><b>{storeOnRoad}</b><span>{t('central.onTheRoad')}</span></div>
          </div>
          <div className="lines">
            {storeRows.length === 0 && <span className="empty">{t('central.noStoreWork')}</span>}
            {storeRows.slice(0, 3).map((e) => (
              <div key={e.storeId}>
                <span>{e.name || '—'}</span>
                {readyCount(e) > 0
                  ? <Tag $tone="blue">{t('central.nReady', { count: readyCount(e) })}</Tag>
                  : e.incoming.length > 0
                    ? <Tag $tone="warn">{t('central.nComing', { count: e.incoming.length })}</Tag>
                    : <Tag>{t('central.nWaiting', { count: e.waiting })}</Tag>}
              </div>
            ))}
          </div>
        </DoorCard>
      )}

      {tabOk('supplier-packages') && (
        <DoorCard type="button" onClick={() => setActiveTab('supplier-packages')} aria-label={`${tabLabel('supplier-packages')}: ${supplierLensTotals.lenses || 0} ${t('central.lensesToOrder')}. ${t('central.openTab', { name: tabLabel('supplier-packages') })}`}>
          {doorHead(Truck, tabLabel('supplier-packages'))}
          <div className="big">
            <strong>{fmtNum(supplierLensTotals.lenses)}</strong><span>{t('central.lensesToOrder')}</span>
            {supplierBatchLate && <Tag $tone="red">{t('central.queueBatchOverdue')}</Tag>}
          </div>
          <div className="mini">
            <div><b>{fmtNum(supplierLensTotals.orders)}</b><span>{t('central.paidOrders')}</span></div>
            <div><b>{supplierOutstanding}</b><span>{t('central.waitingDelivery')}</span></div>
            <div><b>{supplierOldestHours != null ? formatAge(supplierOldestHours) : '—'}</b><span>{t('central.oldestWait')}</span></div>
          </div>
          <div className="lines">
            {supplierLinesByAge.length === 0 && <span className="empty">{t('central.noSupplierLines')}</span>}
            {supplierLinesByAge.slice(0, 3).map((l) => (
              <div key={l.requestId}>
                <span>{l.productName}{l.displayCorrection ? ` · ${l.displayCorrection}` : ''}</span>
                {l.hours != null && <Tag $tone={toneForAge(l.hours) === 'fresh' ? 'grey' : 'warn'}>{formatAge(l.hours)}</Tag>}
              </div>
            ))}
          </div>
        </DoorCard>
      )}

      <DoorCard type="button" onClick={() => setActiveTab('stock-movements')} aria-label={`${tabLabel('stock-movements')}. ${t('central.openTab', { name: tabLabel('stock-movements') })}`}>
        {doorHead(Boxes, tabLabel('stock-movements'))}
        <div className="big"><strong>{stockOverview ? fmtNum(stockOverview.movementsLast7Days) : '—'}</strong><span>{t('central.movementsThisWeek')}</span></div>
        <div className="mini">
          <div><b>{centralStock ? fmtNum(centralStock.lenses) : '—'}</b><span>{t('central.yourLenses')}</span></div>
          <div><b>{centralStock ? fmtNum(centralStock.frames) : '—'}</b><span>{t('central.yourFrames')}</span></div>
          <div><b>{stockOverview ? fmtNum(storeRequestTotal) : '—'}</b><span>{t('central.storeRequestsShort')}</span></div>
        </div>
        <div className="lines">
          {stockMoves.length === 0 && <span className="empty">{t('central.noMovements')}</span>}
          {stockMoves.slice(0, 3).map((m) => (
            <div key={m.id}>
              <span><strong style={{ color: m.qty > 0 ? 'var(--ok)' : 'var(--danger)' }}>{m.qty > 0 ? `+${m.qty}` : `−${Math.abs(m.qty || 0)}`}</strong> {moveLine(m)}</span>
              <span style={{ color: 'var(--ink-mute)', fontSize: '0.75rem' }}>{moveTime(m.at)}</span>
            </div>
          ))}
        </div>
      </DoorCard>
    </DoorGrid>
  );

  const pageCrumb = (id) => (
    <Crumb aria-label={t('central.breadcrumb')}>
      <button type="button" onClick={() => setActiveTab('overview')}>{tabLabel('overview')}</button>
      <span aria-hidden="true">›</span>
      <span>{tabLabel(id)}</span>
    </Crumb>
  );

  const activeTabDef = TAB_DEFS.find((tab) => tab.id === activeTab) || TAB_DEFS[0];

  return (
    <Page $collapsed={navCollapsed}>
      <DesignTokens />

      <Sidebar $collapsed={navCollapsed}>
        <BrandMark $collapsed={navCollapsed}>
          {/* Mark only. The warehouse name already sits in the page header,
              where it can't wrap into the rail. */}
          <div className="dot"><Warehouse size={16} /></div>
          {/* Sits in the brand row so it never scrolls away with the list. */}
          <NavCollapseButton
            type="button"
            $collapsed={navCollapsed}
            onClick={toggleNav}
            aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!navCollapsed}
            title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {navCollapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </NavCollapseButton>
        </BrandMark>

        <NavSection>
          {NAV_GROUPS.map((group) => {
            if (!group.items.some(tabOk)) return null;
            // A group holding the current tab always stays open — folding the
            // page you are looking at is disorienting. Same when the rail is
            // collapsed: there are no headings to unfold it with.
            const gLabel = groupLabel(group);
            const holdsActive = group.items.includes(activeTab);
            const folded = gLabel && !navCollapsed && foldedGroups.has(group.id) && !holdsActive;
            const hiddenWork = folded
              ? group.items.filter((id) => WORK_TABS.has(id)).length
              : 0;
            return (
            <NavGroup key={group.id} className="nav-group">
              {gLabel && (
                <NavGroupLabel
                  type="button"
                  className="nav-group-label"
                  $open={!folded}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!folded}
                  aria-label={folded
                    ? t('central.sectionCollapsed', { name: gLabel })
                    : t('central.sectionExpanded', { name: gLabel })}
                >
                  {gLabel}
                  {hiddenWork > 0 && <span className="folded-count">{hiddenWork}</span>}
                  <ChevronDown size={12} className="chev" aria-hidden="true" />
                </NavGroupLabel>
              )}
              {!folded && group.items.filter(tabOk).map((id) => {
                const tabDef = TAB_DEFS.find((tab) => tab.id === id);
                if (!tabDef) return null;
                const Icon = tabDef.icon;
                const label = tabLabel(tabDef);
                const badge =
                  tabDef.id === 'supplier-lens-orders' ? supplierLensTotals.lenses :
                  tabDef.id === 'activity' ? activities.length :
                  tabDef.id === 'lens-tasks' ? lensTasks.length :
                  tabDef.id === 'incoming' ? incoming.length :
                  tabDef.id === 'supplier-receipt' ? supplierOutstanding :
                  tabDef.id === 'stock-requests' ? stockRequests.length :
                  tabDef.id === 'returns' ? returningOrders.length :
                  tabDef.id === 'outgoing' ? outgoing.length :
                  tabDef.id === 'reservations' ? reservations.length :
                  null;
                const hasBadge = typeof badge === 'number' && badge > 0;
                const isWork = hasBadge && WORK_TABS.has(tabDef.id);
                return (
                  <NavItem
                    key={tabDef.id}
                    active={activeTab === tabDef.id}
                    onClick={() => setActiveTab(tabDef.id)}
                    data-work={isWork ? 'true' : undefined}
                    data-collapsed={navCollapsed ? 'true' : undefined}
                    title={navCollapsed ? label : undefined}
                    aria-current={activeTab === tabDef.id ? 'page' : undefined}
                    aria-label={hasBadge
                      ? `${label}, ${badge}${isWork ? ` ${t('central.needingAction')}` : ''}`
                      : label}
                  >
                    <Icon size={15} aria-hidden="true" />
                    <span className="nav-label">{label}</span>
                    {hasBadge && <span className="badge">{badge}</span>}
                  </NavItem>
                );
              })}
            </NavGroup>
            );
          })}
        </NavSection>

        <NavSpacer />

        <NavFooter>
          <NavItem
            onClick={handleRefresh}
            disabled={loading}
            data-collapsed={navCollapsed ? 'true' : undefined}
            title={navCollapsed ? t('central.refreshData') : undefined}
            aria-label={t('central.refreshData')}
          >
            <RefreshCw size={15} />
            <span className="nav-label">{loading ? t('central.refreshing') : t('central.refreshData')}</span>
          </NavItem>
          <NavItem
            onClick={handleLogout}
            data-collapsed={navCollapsed ? 'true' : undefined}
            title={navCollapsed ? t('central.signOut') : undefined}
            aria-label={t('central.signOut')}
          >
            <LogOut size={15} />
            <span className="nav-label">{t('central.signOut')}</span>
          </NavItem>
        </NavFooter>
      </Sidebar>

      <Workspace>
        <TopBar>
          <div>
            <h1>{tabLabel(activeTabDef)}</h1>
            {/* Who you are, where you are, how fresh this is — the three things
                that are true on every tab, so they belong here rather than
                taking a tile in the metrics row. */}
            <p className="sub">
              <Warehouse size={13} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 5 }} />
              <strong style={{ color: 'var(--ink-soft)', fontWeight: 600 }}>
                {user.store?.name || t('central.centralFallback')}
              </strong>
              {user.store?.storeCode ? ` (${user.store.storeCode})` : ''}
              {user.firstName ? ` · ${user.firstName}` : ''}
              {lastUpdatedAt && (
                <span style={{ color: 'var(--ink-mute)' }}>
                  {' · '}{t('central.updatedAt', { time: lastUpdatedAt.toLocaleTimeString() })}
                </span>
              )}
            </p>
          </div>
          <HeaderActions>
            {['overview', 'store-packages'].includes(activeTab) && (
              <Button className="primary" onClick={() => setShowScanner((v) => !v)} aria-expanded={showScanner}>
                <PackageIcon size={14} />{t('central.scanPackage')}
              </Button>
            )}
            <Button onClick={handleRefresh} disabled={loading}>
              <RefreshCw size={14} />{loading ? t('central.refreshing') : t('central.refresh')}
            </Button>
          </HeaderActions>
        </TopBar>

        <Content>

      {activeTab === 'overview' && (
        <>
          {showScanner && (
            <TabPanel>
              <SectionTitle><PackageIcon size={18} /> {t('central.scanAPackage')}</SectionTitle>
              <PackageScanner
                helpText={t('central.scanPackageHelp')}
                onSuccess={handleRefresh}
              />
            </TabPanel>
          )}
          {overviewQueue}
          {overviewDoors}
        </>
      )}

      {activeTab === 'store-packages' && (
        <>
          {pageCrumb('store-packages')}
          {showScanner && (
            <TabPanel>
              <SectionTitle><PackageIcon size={18} /> {t('central.scanAPackage')}</SectionTitle>
              <PackageScanner
                helpText={t('central.scanPackageHelp')}
                onSuccess={handleRefresh}
              />
            </TabPanel>
          )}
          <TabPanel>
            <QueueHead>
              <SectionTitle as="h2"><Warehouse size={18} /> {tabLabel('store-packages')}</SectionTitle>
              <span className="sub">{t('central.storesHint')}</span>
            </QueueHead>
            {storeNotice && <PageNotice $kind={storeNotice.kind} style={{ marginBottom: '0.75rem' }}>{storeNotice.message}</PageNotice>}
            {storeRows.length === 0 ? (
              <AllClear><CheckCircle2 size={18} /> {t('central.noStoreWork')}</AllClear>
            ) : (
              <StoreList>
                <StoreHead>
                  <span>{t('central.colStore')}</span>
                  <span>{t('central.colReadyToPack')}</span>
                  <span>{t('central.colBoxesOut')}</span>
                  <span>{t('central.colComingIn')}</span>
                  <span>{t('central.colNotReady')}</span>
                  <span />
                </StoreHead>
                {storeRows.map((e) => {
                  const open = openStoreId === e.storeId;
                  const ready = readyCount(e);
                  const picked = pickedFor(e);
                  const pickedTotal = picked.orders.length + picked.transfers.length + picked.supplies.length;
                  return (
                    <React.Fragment key={e.storeId}>
                      <StoreRow type="button" $open={open} aria-expanded={open} onClick={() => setOpenStoreId(open ? null : e.storeId)}>
                        <span className="name">{e.name || '—'}</span>
                        <span>{ready ? <Tag $tone="blue">{t('central.nReady', { count: ready })}</Tag> : <span className="none">{t('central.nothing')}</span>}</span>
                        <span>{e.outgoing.length ? <Tag $tone={e.outgoing.some((p) => p.status === 'at_central_returned') ? 'warn' : 'blue'}>{t('central.nBoxes', { count: e.outgoing.length })}</Tag> : <span className="none">{t('central.none')}</span>}</span>
                        <span className="coming">{e.incoming.length ? <Tag $tone="warn">{t('central.nComing', { count: e.incoming.length })}</Tag> : <span className="none">{t('central.none')}</span>}</span>
                        <span className="waiting">{e.waiting ? fmtNum(e.waiting) : <span className="none">0</span>}</span>
                        <ChevronDown size={16} className="chev" aria-hidden="true" />
                      </StoreRow>
                      {open && (
                        <StoreBox>
                          {e.outgoing.length > 0 && (
                            <>
                              <div className="gh">{t('central.colBoxesOut')}</div>
                              {e.outgoing.map((p) => renderOutBox(p, e.name))}
                              <div className="gh" style={{ marginTop: '1.1rem' }}>{t('central.colReadyToPack')}</div>
                            </>
                          )}
                          {e.orders.length > 0 && <div className="gh">{t('central.groupOrders')}</div>}
                          {e.orders.map((o) => {
                            const customer = o.visitor ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ') : '';
                            return (
                              <label className="it" key={o._id}>
                                <input type="checkbox" checked={!isLeftOut('o', o._id)} onChange={() => toggleLeftOut('o', o._id)} />
                                <strong>{o.orderNumber}</strong>
                                {customer && <span className="soft">{customer}</span>}
                              </label>
                            );
                          })}
                          {e.transfers.length > 0 && <div className="gh">{t('central.groupLenses')}</div>}
                          {groupTransfers(e.transfers).map((g) => {
                            const on = g.ids.some((id) => !isLeftOut('t', id));
                            return (
                              <label className="it" key={g.key}>
                                <input type="checkbox" checked={on} onChange={() => setGroupLeftOut(g.ids, on)} />
                                <span>{g.productName}</span>
                                <span className="soft">{t('central.sph')} {g.sph} {t('central.cyl2')} {g.cyl}</span>
                                <strong>×{g.qty}</strong>
                              </label>
                            );
                          })}
                          {e.supplies.length > 0 && <div className="gh">{t('central.groupAccessories')}</div>}
                          {e.supplies.map((s) => (
                            <label className="it" key={s._id}>
                              <input type="checkbox" checked={!isLeftOut('s', s._id)} onChange={() => toggleLeftOut('s', s._id)} />
                              <span>{s.productName}</span>
                              <span className="soft">×{s.qty}</span>
                            </label>
                          ))}
                          {ready > 0 ? (
                            <div className="foot">
                              <Button className="primary" disabled={!pickedTotal || storeBusy === e.storeId} onClick={() => sendStoreBox(e)}>
                                <Printer size={14} />
                                {storeBusy === e.storeId
                                  ? t('central.sending')
                                  : pickedTotal
                                    ? t('central.sendBox', { count: pickedTotal, store: e.name || '' })
                                    : t('central.tickSomething')}
                              </Button>
                              <span className="note">{t('central.oneBoxNote')}</span>
                            </div>
                          ) : (
                            <div className="note">{t('central.nothingReadyFor', { store: e.name || '' })}</div>
                          )}
                          {e.waiting > 0 && (
                            <div className="note" style={{ marginTop: '0.5rem' }}>
                              <Clock size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} aria-hidden="true" />
                              {t('central.moreNotReady', { count: e.waiting })}
                            </div>
                          )}
                          {e.incoming.length > 0 && (
                            <>
                              <div className="gh" style={{ marginTop: '1rem' }}>{t('central.comingToCentral')}</div>
                              {e.incoming.map((p) => {
                                const st = incomingLabel(p);
                                return (
                                  <div className="pk" key={p._id}>
                                    <strong>{p.packageNumber}</strong>
                                    <Tag $tone={st.tone}>{st.text}</Tag>
                                    <span className="grow" />
                                    {p.status !== 'packaged_at_store' && (
                                      <Button disabled={storeBusy === p._id} onClick={() => receiveStorePackage(p)}>
                                        {p.status === 'at_central' ? t('central.openPackage') : t('central.receiveAndOpen')}
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </StoreBox>
                      )}
                    </React.Fragment>
                  );
                })}
              </StoreList>
            )}
          </TabPanel>
        </>
      )}

      {activeTab === 'supplier-packages' && (
        <>
          {pageCrumb('supplier-packages')}
          <TabPanel>
            <QueueHead>
              <SectionTitle as="h2"><ShoppingCart size={18} /> {t('central.toOrderTitle')}</SectionTitle>
              {supplierBatchLate && <Tag $tone="red">{t('central.queueBatchOverdue')}</Tag>}
            </QueueHead>
            {batchNotice && <PageNotice $kind={batchNotice.kind} style={{ marginBottom: '0.75rem' }}>{batchNotice.message}</PageNotice>}
            {!(supplierLensTotals.lenses > 0) ? (
              <AllClear><CheckCircle2 size={18} /> {t('central.noBatch')}</AllClear>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
                  <strong style={{ fontSize: '1.6rem', letterSpacing: '-0.03em' }}>{fmtNum(supplierLensTotals.lenses)}</strong>
                  <span style={{ color: 'var(--ink-soft)' }}>{t('central.lensesToOrder')} · {t('central.kpiSupplierHint', { count: supplierLensTotals.orders || 0 })}</span>
                </div>
                {nextSupplierBatch && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.8rem 0.9rem', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
                    <span style={{ fontWeight: 600 }}>{t('central.nextBatchTitle')}</span>
                    <span style={{ color: 'var(--ink-soft)' }}>{nextSupplierBatch.label} · {t('central.nLenses', { count: nextSupplierBatch.lensCount || 0 })} · {formatDue(supplierBatchDue)}</span>
                    <span style={{ flex: 1 }} />
                    <Button onClick={() => printSupplierLensBatch(nextSupplierBatch)}><Printer size={14} />{t('central.printList')}</Button>
                    <Button className="primary" disabled={batchBusy} onClick={() => markBatchOrdered(nextSupplierBatch)}>
                      {batchBusy ? t('central.sending') : t('central.markBatchOrdered')}
                    </Button>
                  </div>
                )}
                <Button style={{ marginTop: '0.75rem' }} onClick={() => setActiveTab('supplier-lens-orders')}>
                  {t('central.openOrderList')} <ArrowRight size={14} />
                </Button>
              </>
            )}
          </TabPanel>
          <TabPanel>
            <SectionTitle><Truck size={18} /> {t('central.waitingDeliveryTitle')}</SectionTitle>
            <SupplierReceipt headless onDone={handleRefresh} />
          </TabPanel>
        </>
      )}

      {activeTab === 'stock-movements' && (
        <>
          {pageCrumb('stock-movements')}
          {!stockOverview ? (
            <TabPanel><div style={{ color: 'var(--ink-mute)' }}>{lastUpdatedAt ? t('central.stockLoadFailed') : t('central.refreshing')}</div></TabPanel>
          ) : (
            // An open store takes the full width right under the table;
            // Latest movements moves below it while the store is open.
            <SplitGrid style={stockStore ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
              <TabPanel style={{ overflowX: 'auto' }}>
                <SectionTitle><Boxes size={18} /> {t('central.stockByLocation')}</SectionTitle>
                <Table>
                  <thead>
                    <tr>
                      <th>{t('central.colLocation')}</th>
                      <th style={{ textAlign: 'right' }}>{t('central.colLenses')}</th>
                      <th style={{ textAlign: 'right' }}>{t('central.colFrames')}</th>
                      <th>{t('central.colAsking')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {stockLocations.map((l) => (
                      <tr
                        key={l.storeId}
                        onClick={l.isCentral ? undefined : () => toggleStockStore(l)}
                        style={l.isCentral ? { background: 'var(--accent-soft)' } : { cursor: 'pointer', background: stockStore?.storeId === l.storeId ? 'var(--surface-2, #f8fafc)' : undefined }}
                      >
                        <td>
                          {!l.isCentral && (stockStore?.storeId === l.storeId
                            ? <ChevronDown size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                            : <ChevronRight size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />)}
                          <strong style={{ color: 'var(--ink)' }}>{l.name}</strong>
                          {l.isCentral && <Tag $tone="blue" style={{ marginLeft: 6 }}>{t('central.you')}</Tag>}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: l.lenses ? 'var(--ink)' : 'var(--ink-mute)' }}>{fmtNum(l.lenses)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: l.frames ? 'var(--ink)' : 'var(--ink-mute)' }}>{fmtNum(l.frames)}</td>
                        <td style={{ color: (l.requests || l.refill) ? 'var(--ink-2)' : 'var(--ink-mute)' }}>
                          {[
                            l.requests ? t('central.askingOrders', { count: fmtNum(l.requests) }) : null,
                            l.refill ? t('central.askingRefill', { count: fmtNum(l.refill) }) : null,
                          ].filter(Boolean).join(' · ') || t('central.nothing')}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {l.isCentral ? (
                            <>
                              {tabOk('lens-stock') && <Button onClick={() => setActiveTab('lens-stock')}>{t('central.lensStockBtn')}</Button>}{' '}
                              {tabOk('frame-stock') && <Button onClick={() => setActiveTab('frame-stock')}>{t('central.frameStockBtn')}</Button>}
                            </>
                          ) : ((l.requests || l.refill) > 0 && tabOk('stock-requests') && (
                            <Button onClick={(ev) => { ev.stopPropagation(); setActiveTab('stock-requests'); }}>{t('central.requestsBtn')}</Button>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TabPanel>
              {renderStoreStockPanel()}
              <TabPanel>
                <SectionTitle><History size={18} /> {t('central.latestMovements')}</SectionTitle>
                <div style={{ color: 'var(--ink-soft)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  {t('central.movementsCount', { count: fmtNum(stockOverview.movementsLast7Days) })}
                </div>
                {/* Downloaded as Excel rather than listed here: one line per
                    movement with store, product, power, order and who. */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {[7, 30, 90].map((days) => (
                    <Button key={days} className={movesDays === days ? 'primary' : undefined} onClick={() => setMovesDays(days)}>
                      {t('central.lastNDays', { count: days })}
                    </Button>
                  ))}
                  <span style={{ flex: 1 }} />
                  <Button className="primary" disabled={movesDownloading} onClick={downloadMovements}>
                    <Download size={14} /> {movesDownloading ? t('central.sending') : t('central.downloadExcel')}
                  </Button>
                </div>
              </TabPanel>
            </SplitGrid>
          )}
        </>
      )}

      {activeTab === 'activity' && (
        <TabPanel>
          <SectionTitle><History size={18} /> {t('central.centralActivity')}{activities.length})</SectionTitle>
          <div style={{ color: '#64748b', fontSize: '0.86rem', marginBottom: '1rem' }}>
            {t('central.everyCentralPackageScanPackage')}
          </div>
          {renderActivityFeed()}
        </TabPanel>
      )}

      {activeTab === 'incoming' && (
        <>
          {/* Packages arriving from stores are received on Store packages. */}
          <TabPanel>
            <SectionTitle><Inbox size={18} /> {t('central.incomingOrders')}</SectionTitle>
            {renderOrdersTable(incoming, t('central.nothingIncoming'))}
          </TabPanel>
        </>
      )}

      {activeTab === 'lens-tasks' && (
        <TabPanel>
          <SectionTitle><ShoppingCart size={18} /> {t('central.lensTasks')}{lensTasks.length})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 760 }}>
            {t('central.confirmReservedStockLensesMark')}
          </div>
          {renderLensTasks()}
        </TabPanel>
      )}

      {activeTab === 'supplier-lens-orders' && (
        <TabPanel>
          <SectionTitle><ShoppingCart size={18} /> {t('central.lensOrderLists')}{supplierLensTotals.lenses || 0})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 820 }}>
            {t('central.theseAreOnlyPaidLens')}
          </div>
          {renderSupplierLensOrders()}
        </TabPanel>
      )}

      {activeTab === 'supplier-receipt' && (
        <TabPanel>
          <SectionTitle><Truck size={18} /> {t('central.supplierDelivery')}{supplierOutstanding})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 820 }}>
            {t('central.countInABoxFrom')}
          </div>
          <SupplierReceipt headless onDone={() => setRefreshKey((k) => k + 1)} />
        </TabPanel>
      )}

      {activeTab === 'stock-requests' && (
        <TabPanel>
          <SectionTitle><ListChecks size={18} /> {t('central.storeStockRequests')}{stockRequests.length})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 760 }}>
            {t('central.requestsFromStoresToCentral')}
          </div>
          {stockRequestNotice && (
            <PkgNotice kind={stockRequestNotice.kind}>
              <span>{stockRequestNotice.message}</span>
            </PkgNotice>
          )}
          {/* One card per store: the box, its contents, and what is staying
              behind. Every store reads the same way whether or not it has
              anything ready. */}
          {outgoingByStore.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '0.85rem', padding: '1rem 0', marginBottom: '0.5rem' }}>
              {t('central.nothingWaitingToGoOut')}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '1rem' }}>
              {outgoingByStore.map((row) => (
                <PkgCard key={row.storeId}>
                  <h3>
                    <PackageIcon size={16} color={PRIMARY} />
                    {row.storeName || 'Unknown store'}
                    {row.pkg && <span className="num">{row.pkg.packageNumber}</span>}
                  </h3>
                  <p className="sub">
                    {row.pkg?.itemCount
                      ? <>{t('central.collectingForThisStoreTap')} <strong>{t('central.sendToOutgoing')}</strong> {t('central.whenTheCourierComesThe')}</>
                      : <>{t('central.nothingReadyForThisStore')}</>}
                  </p>

                  <PkgList>
                    {groupLines(row.ready).map((r) => (
                      <PkgRow key={r.ids.join(':')}>
                        <div>
                          <strong>{r.qty}× {r.productName || 'Lens'}</strong>
                          <div className="meta">{r.displayCorrection || 'No correction recorded'}</div>
                        </div>
                        {/* Only a legacy auto-opened box can say "in the
                            package" now; otherwise the line is on the bench
                            waiting for someone to pack it. */}
                        <PkgPill $tone="ready">
                          {row.pkg ? t('central.inThePackage') : t('central.readyToPack', 'Ready to pack')}
                        </PkgPill>
                      </PkgRow>
                    ))}
                    {groupLines(row.waiting).map((r) => (
                      <PkgRow key={r.ids.join(':')}>
                        <div>
                          <strong>{r.qty}× {r.productName || 'Lens'}</strong>
                          <div className="meta">{r.displayCorrection || 'No correction recorded'}</div>
                        </div>
                        <PkgPill $tone="waiting">{String(r.status || '').replace(/_/g, ' ')}</PkgPill>
                      </PkgRow>
                    ))}
                    {!row.ready.length && !row.waiting.length && (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>
                        {t('central.nothingInThisPackageYet')}
                      </div>
                    )}
                  </PkgList>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.875rem' }}>
                    {/* Boxes are built in one place now. A store's lenses and its
                        finished customer orders go in the same box, and only the
                        Returns tab can see both, so this sends you there rather
                        than closing a lens-only box from here. `row.pkg` only
                        exists for boxes auto-opened before that change. */}
                    {row.pkg ? (
                      <Button
                        className="primary"
                        onClick={() => sendOpenPackage(row.pkg)}
                        disabled={stockRequestBusy || !row.pkg?.itemCount}
                        title="Legacy open box — close it and move it to Outgoing for courier pickup"
                      >
                        <PackageIcon size={15} style={{ marginRight: 5 }} />
                        {t('central.sendToOutgoing')}{row.pkg?.itemCount ? ` (${row.pkg.itemCount})` : ''}
                      </Button>
                    ) : (
                      <Button
                        className="primary"
                        onClick={() => setActiveTab('returns')}
                        disabled={!row.ready.length}
                        title={row.ready.length
                          ? 'Pack these together with this store’s finished orders in the Returns tab'
                          : 'Nothing ready for this store yet'}
                      >
                        <PackageIcon size={15} style={{ marginRight: 5 }} />
                        Pack in Returns{row.ready.length ? ` (${row.ready.length})` : ''}
                      </Button>
                    )}
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                      {row.ready.length} ready to pack · {row.waiting.length} waiting
                    </span>
                  </div>
                </PkgCard>
              ))}
            </div>
          )}
          {stockRequests.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
              {t('central.noStockRequestsYet')}
            </div>
          )}

          {/* One grid per lens per store, laid out side by side — each card is
              one matrix wide, so several fit across before wrapping. */}
          <ReqGrid>
          {lensRequestGroups.map((group) => {
            const pickable = group.requests.filter((r) => READY_STATUSES.includes(r.status) && !r.package);
            const allPicked = pickable.length > 0 && pickable.every((r) => pickedTransferIds[r._id]);
            const totalQty = group.requests.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
            return (
              <ReqGroup key={group.key}>
                <ReqGroupHead>
                  <div>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>{group.productName}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {group.storeName} · {group.requests.length} {t('central.cellS')} {totalQty} {t('central.unitS')}
                      {group.sku ? ` · ${group.sku}` : ''}
                      {group.offGrid > 0 && (
                        <span style={{ color: '#b45309', fontWeight: 700 }}>
                          {' '}· {group.offGrid} {t('central.outsideSph66Cyl')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {pickable.length > 0 && (
                      <Button
                        onClick={() => setPickedTransferIds((m) => {
                          const next = { ...m };
                          pickable.forEach((r) => { next[r._id] = !allPicked; });
                          return next;
                        })}
                      >
                        {allPicked ? t('central.deselectAll') : `Select ${pickable.length} ready`}
                      </Button>
                    )}
                    {group.requests.some((r) => r.status === 'supplier_order_required') && (
                      <Button onClick={() => updateGroupStatus(group, 'supplier_ordered')} disabled={stockRequestBusy}>{t('central.ordered')}</Button>
                    )}
                    {group.requests.some((r) => r.status === 'supplier_ordered') && (
                      <Button onClick={() => updateGroupStatus(group, 'supplier_arrived')} disabled={stockRequestBusy}>{t('central.arrived')}</Button>
                    )}
                    {group.requests.some((r) => r.status === 'supplier_arrived') && (
                      <Button onClick={() => updateGroupStatus(group, 'ready_for_package')} disabled={stockRequestBusy}>{t('central.ready')}</Button>
                    )}
                    <Button onClick={() => updateGroupStatus(group, 'cancelled')} disabled={stockRequestBusy}>{t('central.cancelAll')}</Button>
                  </div>
                </ReqGroupHead>

                <ReqTableWrap>
                  <ReqTable>
                    {/* Same three-row head as the Lens Stock matrix: lens name
                        across the top, then SPH holding the corner while CYL
                        spans its own numbers. */}
                    <thead>
                      <tr>
                        <th colSpan={LENS_CYL_VALUES.length + 1}>{group.productName}</th>
                      </tr>
                      <tr>
                        <th rowSpan={2}>{t('central.sph')}</th>
                        <th colSpan={LENS_CYL_VALUES.length}>{t('central.cyl')}</th>
                      </tr>
                      <tr>
                        {LENS_CYL_VALUES.map((cyl) => (
                          <th key={cyl}>{formatCylLabel(cyl)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {LENS_SPH_VALUES.map((sph) => (
                        <tr key={sph}>
                          <ReqSphHeader $value={Number(sph)}>{Number(sph).toString()}</ReqSphHeader>
                          {LENS_CYL_VALUES.map((cyl) => {
                            const atCell = group.byCell.get(`${lensKey(sph)}:${lensKey(cyl)}`);
                            // Empty cells keep the stock table's zone banding so
                            // the two grids read as the same sheet.
                            if (!atCell?.length) return <td key={cyl}><ReqEmptyCell $bg={zoneBg(sph, cyl)} /></td>;
                            const cellQty = atCell.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
                            const pickable = atCell.filter((r) => READY_STATUSES.includes(r.status) && !r.package);
                            const selectable = pickable.length > 0;
                            // A part-covered cell takes the colour of the part
                            // still outstanding — central needs to see at a
                            // glance that it isn't finished, even though some of
                            // it can ship today.
                            const outstanding = atCell.find((r) => !READY_STATUSES.includes(r.status));
                            const tone = reqTone((outstanding || atCell[0]).status);
                            const allPicked = selectable && pickable.every((r) => pickedTransferIds[r._id]);
                            // Ready units are already in the store's open
                            // package, so say so rather than inviting a pick
                            // that has nothing left to do.
                            const breakdown = atCell
                              .map((r) => `${r.qty} ${reqTone(r.status).label}${r.package ? ` (in ${r.package.packageNumber || 'package'})` : ''}`)
                              .join(', ');
                            return (
                              <td key={cyl}>
                                <ReqCell
                                  type="button"
                                  $bg={tone.bg}
                                  $color={tone.color}
                                  $picked={allPicked}
                                  $selectable={selectable}
                                  onClick={() => {
                                    if (!selectable) return;
                                    setPickedTransferIds((m) => {
                                      const next = { ...m };
                                      pickable.forEach((r) => { next[r._id] = !allPicked; });
                                      return next;
                                    });
                                  }}
                                  title={`SPH ${sph} / CYL ${cyl} — ${cellQty} unit(s): ${breakdown}${selectable ? '. Click to add the ready units to the package.' : ''}`}
                                >
                                  {cellQty}
                                </ReqCell>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </ReqTable>
                </ReqTableWrap>
              </ReqGroup>
            );
          })}
          </ReqGrid>

          {/* Frames, accessories and consumables have no SPH/CYL to plot. */}
          {nonLensRequests.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>{t('central.store')}</th>
                  <th>{t('central.type')}</th>
                  <th>{t('central.product')}</th>
                  <th>{t('central.qty')}</th>
                  <th>{t('central.status')}</th>
                  <th>{t('central.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {nonLensRequests.map((r) => {
                  const ready = READY_STATUSES.includes(r.status);
                  return (
                    <tr key={r._id}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!ready || !!r.package}
                          checked={!!pickedTransferIds[r._id]}
                          onChange={(e) => setPickedTransferIds((m) => ({ ...m, [r._id]: e.target.checked }))}
                        />
                      </td>
                      <td>{r.requestingStore?.name || 'Store'}</td>
                      <td>{r.type}</td>
                      <td>
                        <strong>{r.productName}</strong>
                        {r.sku && <div className="muted">{r.sku}</div>}
                      </td>
                      <td>{r.qty}</td>
                      <td><StatusPill bg="#e0e7ff" color="#3730a3">{String(r.status).replace(/_/g, ' ')}</StatusPill></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {r.status === 'supplier_order_required' && (
                            <Button onClick={() => updateStockRequestStatus(r, 'supplier_ordered')} disabled={stockRequestBusy}>{t('central.ordered')}</Button>
                          )}
                          {r.status === 'supplier_ordered' && (
                            <Button onClick={() => updateStockRequestStatus(r, 'supplier_arrived')} disabled={stockRequestBusy}>{t('central.arrived')}</Button>
                          )}
                          {r.status === 'supplier_arrived' && (
                            <Button onClick={() => updateStockRequestStatus(r, 'ready_for_package')} disabled={stockRequestBusy}>{t('central.ready')}</Button>
                          )}
                          {!['received', 'cancelled', 'packaged', 'in_transit'].includes(r.status) && (
                            <Button onClick={() => updateStockRequestStatus(r, 'cancelled')} disabled={stockRequestBusy}>{t('central.cancel')}</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </TabPanel>
      )}

      {activeTab === 'returns' && (
        <TabPanel>
          <SectionTitle><SafeOutbox size={18} /> {t('central.returnsFromLab')}{returningOrders.length})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 720 }}>
            {t('central.bundleFinishedCustomerOrdersAnd')}
          </div>
          {returnNotice && (
            <div style={{
              background: returnNotice.kind === 'success' ? '#dcfce7' : '#fee2e2',
              color: returnNotice.kind === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${returnNotice.kind === 'success' ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: 10,
              padding: '0.55rem 0.8rem',
              fontSize: '0.85rem',
              marginBottom: '0.9rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              {returnNotice.kind === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              <span>{returnNotice.message}</span>
              {returnNotice.package && (
                <Button
                  style={{ marginLeft: 'auto' }}
                  onClick={() => printTransferPackageLabel(returnNotice.package)}
                >
                  <Printer size={12} style={{ marginRight: 4 }} /> {t('central.reprintLabel')}
                </Button>
              )}
            </div>
          )}
          {returnsByStore.length === 0 ? (
            <div style={{ padding: '1rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {t('central.nothingToSendBackYet')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {returnsByStore.map((group) => {
                const picked = pickedCountForStore(group.storeId);
                return (
                  <div
                    key={group.storeId}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 14,
                      background: 'white',
                      padding: '1rem 1.1rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.7rem' }}>
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                          {group.store?.name || 'Unknown store'} · {group.orders.length} order{group.orders.length === 1 ? '' : 's'} · {group.transfers.length} {t('central.lensTransfer')}{group.transfers.length === 1 ? '' : 's'}{group.supplies.length ? ` · ${group.supplies.length} accessor${group.supplies.length === 1 ? 'y' : 'ies'}` : ''}
                        </div>
                        {group.store?.storeCode && (
                          <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{group.store.storeCode}</div>
                        )}
                      </div>
                      <Button
                        onClick={() => createReturnPackage(group.storeId)}
                        disabled={returnBusy || picked === 0}
                        style={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: 'white',
                          border: 'none',
                          fontWeight: 700,
                          opacity: returnBusy || picked === 0 ? 0.6 : 1,
                        }}
                      >
                        <PackageIcon size={13} style={{ marginRight: 4 }} />
                        {returnBusy ? t('central.creating') : `Combine into package (${picked})`}
                      </Button>
                    </div>
                    <Table>
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th>{t('central.type')}</th>
                          <th>{t('central.reference')}</th>
                          <th>{t('central.details')}</th>
                          <th>{t('central.owner')}</th>
                          <th>{t('central.updated')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.orders.map((o) => {
                          const checked = !!pickedReturnIds[o._id];
                          const customer = o.visitor
                            ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ')
                            : '—';
                          return (
                            <tr key={o._id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => setPickedReturnIds((m) => ({ ...m, [o._id]: e.target.checked }))}
                                />
                              </td>
                              <td>{t('central.order2')}</td>
                              <td><strong style={{ color: '#0f172a' }}>{o.orderNumber}</strong></td>
                              <td>{customer}</td>
                              <td>{o.salesPerson ? `${o.salesPerson.firstName || ''} ${o.salesPerson.lastName || ''}`.trim() : '—'}</td>
                              <td className="muted">{o.updatedAt ? new Date(o.updatedAt).toLocaleString() : ''}</td>
                            </tr>
                          );
                        })}
                        {group.transfers.map((r) => {
                          const checked = !!pickedReturnTransferIds[r._id];
                          return (
                            <tr key={r._id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => setPickedReturnTransferIds((m) => ({ ...m, [r._id]: e.target.checked }))}
                                />
                              </td>
                              <td>{t('central.lensTransfer2')}</td>
                              <td><strong style={{ color: '#0f172a' }}>{r.productName}</strong></td>
                              <td>{t('central.sph')} {r.sph} {t('central.cyl2')} {r.cyl} {t('central.qty2')} {r.qty}</td>
                              <td>{r.requestedBy ? `${r.requestedBy.firstName || ''} ${r.requestedBy.lastName || ''}`.trim() : t('central.storeRequest')}</td>
                              <td className="muted">{r.updatedAt ? new Date(r.updatedAt).toLocaleString() : ''}</td>
                            </tr>
                          );
                        })}
                        {/* Accessories. The third thing that travels to a store
                            and, until the box was unified, the one that
                            travelled in no box at all. */}
                        {group.supplies.map((sup) => {
                          const checked = !!pickedReturnSupplyIds[sup._id];
                          return (
                            <tr key={sup._id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => setPickedReturnSupplyIds((m) => ({ ...m, [sup._id]: e.target.checked }))}
                                />
                              </td>
                              <td>Accessory</td>
                              <td><strong style={{ color: '#0f172a' }}>{sup.productName}</strong></td>
                              <td>Qty {sup.qty}{sup.requestNumber ? ` · ${sup.requestNumber}` : ''}</td>
                              <td>{String(sup.status || '').replace(/_/g, ' ')}</td>
                              <td className="muted">{sup.updatedAt ? new Date(sup.updatedAt).toLocaleString() : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                );
              })}
            </div>
          )}
        </TabPanel>
      )}

      {activeTab === 'outgoing' && (
        <TabPanel>
          <SectionTitle><SafeOutbox size={18} /> {t('central.outgoingPackages')}{outgoingPackages.length})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 720 }}>
            {t('central.packagesCreatedAtCentralAnd')}
          </div>
          <Table>
            <thead>
              <tr>
                <th>{t('central.package')}</th>
                <th>{t('central.destinationStore')}</th>
                <th>{t('central.type')}</th>
                <th>{t('central.contents')}</th>
                <th>{t('central.status')}</th>
                <th>{t('central.updated')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {outgoingPackages.length === 0 ? (
                <tr><td className="muted" colSpan={7}>{t('central.noPackagesHeadedOut')}</td></tr>
              ) : outgoingPackages.map((pkg) => {
                const isTransfer = pkg.packageType === 'stock_transfer';
                return (
                  <tr key={pkg._id}>
                    <td><strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{pkg.packageNumber}</strong></td>
                    <td>{pkg.originStore?.name || 'Store'}</td>
                    <td>{isTransfer ? t('central.stockTransfer') : t('central.customerOrder')}</td>
                    <td>
                      {(pkg.orders?.length || 0) > 0 && (
                        <div>{pkg.orders.length} order{pkg.orders.length === 1 ? '' : 's'}</div>
                      )}
                      {/* Name and power, not a count — the person packing the
                          box needs to know which lens is in it. Same row styling
                          as the store's package list. */}
                      {(pkg.stockTransferRequests?.length || 0) > 0 ? (
                        <PkgList style={{ marginTop: (pkg.orders?.length || 0) > 0 ? 6 : 0 }}>
                          {pkg.stockTransferRequests.map((t) => (
                            <PkgRow key={t._id}>
                              <div>
                                <strong>{t.productName || 'Lens'}</strong>
                                <div className="meta">
                                  qty {t.qty}{t.displayCorrection ? ` · ${t.displayCorrection}` : ''}
                                </div>
                              </div>
                            </PkgRow>
                          ))}
                        </PkgList>
                      ) : (
                        (pkg.orders?.length || 0) === 0 && <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <PkgPill $tone={pkg.status === 'delivered_to_store' ? 'ready' : 'transit'}>
                        {String(pkg.status || '').replace(/_/g, ' ')}
                      </PkgPill>
                    </td>
                    <td className="muted">{formatDateTime(pkg.updatedAt || pkg.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {isTransfer ? (
                          <Button onClick={() => printTransferPackageLabel(pkg)}>
                            <Printer size={12} style={{ marginRight: 4 }} /> {t('central.print')}
                          </Button>
                        ) : (
                          <Button onClick={() => printTransferPackageLabel(pkg)}>
                            <Printer size={12} style={{ marginRight: 4 }} /> {t('central.print')}
                          </Button>
                        )}
                        {pkg.status === 'at_central_returned' && (
                          <Button
                            type="button"
                            className="primary"
                            disabled={thirdPartyBusyId === pkg._id}
                            onClick={() => setHandoffTarget(pkg)}
                            title={t('central.useThisWhenTheInternal')}
                          >
                            <SafeOutbox size={12} style={{ marginRight: 4 }} />
                            {thirdPartyBusyId === pkg._id ? t('central.handing') : t('central.handedTo3rdPartyCourier')}
                          </Button>
                        )}
                        <Button onClick={() => openPackageDetail(pkg)}>{t('central.open')}</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TabPanel>
      )}

      {activeTab === 'reservations' && (
        <TabPanel>
          <SectionTitle><Clock size={18} /> {t('central.activeLensReservations')}{reservations.length})</SectionTitle>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.9rem', maxWidth: 780 }}>
            {t('central.everyLensCurrentlyHeldOut')} <strong>{t('central.noOrder')}</strong> {t('central.areUnitsLockedOnThe')}
          </div>
          {reservationNotice && (
            <div style={{
              background: reservationNotice.kind === 'success' ? '#dcfce7' : '#fee2e2',
              color: reservationNotice.kind === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${reservationNotice.kind === 'success' ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: 10,
              padding: '0.55rem 0.8rem',
              fontSize: '0.85rem',
              marginBottom: '0.9rem',
            }}>{reservationNotice.message}</div>
          )}
          {renderReservations()}
        </TabPanel>
      )}

      {activeTab === 'lens-stock' && (
        <TabPanel>
          {/* Embeds the existing lens-stock UI; resolveAllowedLocation
              on the server scopes inventory/operations managers to
              their assigned (central) Store automatically. */}
          <LensStockPage refreshKey={refreshKey} />
        </TabPanel>
      )}

      {activeTab === 'frame-stock' && (
        <TabPanel>
          <FrameStockPage refreshKey={refreshKey} />
        </TabPanel>
      )}

      {/* Central is the only place these inventories can be topped up, so the
          same page renders its editable variant here (server decides, via
          canRestock) and the read-only + request variant in the store. */}
      {activeTab === 'accessories' && (
        <TabPanel>
          <SupplyStockPage category="accessory" refreshKey={refreshKey} />
        </TabPanel>
      )}

      {activeTab === 'consumables' && (
        <TabPanel>
          <SupplyStockPage category="consumable" refreshKey={refreshKey} />
        </TabPanel>
      )}

      {selectedPackage && (
        <PackageDetailModal
          pkg={selectedPackage}
          loading={packageDetailLoading}
          onClose={closePackageDetail}
          onUnpack={unpackSelectedPackage}
          onHandoffOrder={handoffOrderToLab}
          unpacking={unpacking}
          handoffOrderId={handoffOrderId}
          notice={unpackNotice}
        />
      )}
        </Content>
      </Workspace>

      <ThirdPartyHandoffModal
        open={Boolean(handoffTarget)}
        packageNumber={handoffTarget?.packageNumber}
        busy={thirdPartyBusyId === handoffTarget?._id}
        onCancel={() => setHandoffTarget(null)}
        onConfirm={handoffToThirdPartyCourier}
      />
    </Page>
  );

};

export default CentralStoreDashboard;

// ─── PackageDetailModal ─────────────────────────────────────────────
//
// Modal that shows a package's contents (orders, customers, items)
// and exposes the "Open package" action. Once opened, every order
// gets an inline handoff action plus a printable QR label for lab
// receipt.
const PackageDetailModal = ({ pkg, loading, onClose, onUnpack, onHandoffOrder, unpacking, handoffOrderId, notice }) => {
  const { t } = useTranslation();
  const opened = Boolean(pkg.unpackedAt);
  const isLabHandoffDone = (status) => [
    'in_transit_to_lab',
    'at_lab',
    'in_production',
    'manufacturing',
    'quality_check',
    'returning_to_central',
    'at_central_outbound',
    'in_transit_to_store',
    'ready_for_pickup',
    'out_for_delivery',
    'delivered',
    'completed',
  ].includes(status);

  const printOrderLabel = (order) => {
    const customer = order.visitor
      ? `${order.visitor.firstName || ''} ${order.visitor.lastName || ''}`.trim()
        || (order.visitor.gender || '')
      : '';
    const items = Array.isArray(order.items) && order.items.length
      ? order.items.map((it) => `<li>${(it.itemType || '').toUpperCase()}: ${it.name || ''}${it.brand ? ` (${it.brand})` : ''}${it.quantity > 1 ? ` ×${it.quantity}` : ''}</li>`).join('')
      : '<li style="color:#94a3b8;">No items recorded</li>';
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${order.orderNumber}</title>
<style>
  *{box-sizing:border-box;}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;margin:0;padding:24px;color:#0f172a;}
  h1{margin:0 0 4px;font-size:22px;font-family:ui-monospace,monospace;}
  .sub{color:#475569;font-size:13px;margin-bottom:16px;}
  .row{display:flex;gap:24px;align-items:flex-start;}
  .qr{flex:0 0 auto;border:1px solid #e2e8f0;border-radius:12px;padding:12px;text-align:center;}
  .qr img{width:200px;height:200px;display:block;}
  .qr code{display:block;margin-top:8px;font-size:11px;color:#475569;word-break:break-all;}
  .meta{flex:1;font-size:13px;}
  .meta .label{color:#64748b;text-transform:uppercase;font-size:10px;letter-spacing:.05em;font-weight:700;margin-top:8px;}
  .meta .value{font-weight:600;}
  ul{padding-left:18px;margin:6px 0;}
  .footer{margin-top:18px;color:#64748b;font-size:11px;border-top:1px dashed #cbd5e1;padding-top:8px;}
  @media print{body{padding:8mm;}}
</style></head><body>
  <h1>${order.orderNumber}</h1>
  <div class="sub">Package ${pkg.packageNumber} · ${pkg.originStore?.name || ''}</div>
  <div class="row">
    <div class="qr">
      ${order.qrDataUrl
        ? `<img src="${order.qrDataUrl}" alt="QR" />`
        : `<div style="width:200px;height:200px;display:flex;align-items:center;justify-content:center;color:#94a3b8;border:1px dashed #cbd5e1;">QR unavailable</div>`}
      <code>${order.orderNumber}</code>
    </div>
    <div class="meta">
      <div class="label">Customer</div>
      <div class="value">${customer || '—'}</div>
      <div class="label">Sales person</div>
      <div class="value">${order.salesPerson ? `${order.salesPerson.firstName || ''} ${order.salesPerson.lastName || ''}`.trim() : '—'}</div>
      <div class="label">Items</div>
      <ul>${items}</ul>
    </div>
  </div>
  <div class="footer">Lab scans this QR (or types the order number) to receive the order.</div>
</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    let printed = false;
    const go = () => {
      if (printed) return;
      printed = true;
      setTimeout(() => {
        try { iframe.contentWindow.focus(); } catch (_) {}
        try { iframe.contentWindow.print(); } catch (_) {}
        setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1500);
      }, 600);
    };
    iframe.onload = go;
    setTimeout(go, 1800);
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalShell onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <h2>{t('central.package2')} {pkg.packageNumber}</h2>
            <div className="sub">
              {pkg.orders?.length || 0} {t('central.orderS')}
              {pkg.originStore?.name ? ` · from ${pkg.originStore.name}` : ''}
              {pkg.unpackedAt ? ` · opened ${formatDateTime(pkg.unpackedAt)}` : t('central.sealed2')}
            </div>
          </div>
          <TinyButton onClick={onClose}><X size={14} /> {t('central.close')}</TinyButton>
        </ModalHead>

        <ModalBody>
          {notice && (
            <div style={{
              background: notice.kind === 'success' ? '#dcfce7' : '#fee2e2',
              color: notice.kind === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${notice.kind === 'success' ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: '10px',
              padding: '0.55rem 0.8rem',
              fontSize: '0.85rem',
            }}>
              {notice.message}
            </div>
          )}

          {!opened && (
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              color: '#854d0e',
              borderRadius: 12,
              padding: '0.75rem 0.9rem',
              fontSize: '0.85rem',
            }}>
              <strong>{t('central.sealedPackage')}</strong> {t('central.openItFirstToConfirm')}
            </div>
          )}

          <div>
            <SectionTitle>
              <ListChecks size={18} /> {t('central.contents')}
            </SectionTitle>

            {loading ? (
              <div style={{ padding: '1rem', color: '#64748b' }}>
                <RefreshCw size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /> {t('central.loading')}
              </div>
            ) : (pkg.orders?.length || 0) === 0 ? (
              <div style={{ padding: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                {t('central.packageHasNoOrders')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {pkg.orders.map((o, idx) => {
                  const customer = o.visitor
                    ? `${o.visitor.firstName || ''} ${o.visitor.lastName || ''}`.trim()
                      || (o.visitor.gender || '')
                    : '';
                  const itemSummary = Array.isArray(o.items) && o.items.length
                    ? o.items.map((it) => it.name || it.itemType).filter(Boolean).join(' · ')
                    : 'No items';
                  const handedToLab = isLabHandoffDone(o.status);
                  const handingThisOrder = handoffOrderId === o._id;
                  return (
                    <OrderRow key={o._id || idx} opened={opened}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="num">{o.orderNumber || `Draft ${idx + 1}`}</div>
                        <div className="meta">
                          {customer || 'Visitor'} · {itemSummary}
                          {opened && (
                            <span style={{ marginLeft: 8, color: handedToLab ? '#166534' : '#92400e' }}>
                              {handedToLab ? t('central.handedToLab') : t('central.notHandedToLab')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="right">
                        {opened ? (
                          <>
                            <TinyButton
                              onClick={() => onHandoffOrder(o)}
                              disabled={handedToLab || handingThisOrder}
                              title={handedToLab ? 'Already handed to lab' : 'Move this order to lab waiting queue'}
                            >
                              <HelpingHand size={12} />
                              {handingThisOrder ? t('central.handing') : handedToLab ? t('central.handed') : t('central.handOverEquipmentToLab')}
                            </TinyButton>
                            <TinyButton onClick={() => printOrderLabel(o)}>
                              <Printer size={12} /> {t('central.printLabel')}
                            </TinyButton>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {t('central.openPackageToHandOver')}
                          </span>
                        )}
                      </div>
                    </OrderRow>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <SectionTitle>
              <History size={18} /> {t('central.packageActivity')}
            </SectionTitle>
            {!pkg.history?.length ? (
              <div style={{ padding: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                {t('central.noPackageActivityYet')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {[...(pkg.history || [])]
                  .sort((a, b) => new Date(b.scannedAt || 0) - new Date(a.scannedAt || 0))
                  .map((entry, idx) => {
                    const actor = entry.scannedBy
                      ? `${entry.scannedBy.firstName || ''} ${entry.scannedBy.lastName || ''}`.trim() || entry.scannedBy.email || 'Unknown user'
                      : 'System';
                    return (
                      <div
                        key={`${entry.status}-${entry.scannedAt || idx}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '0.75rem',
                          padding: '0.7rem 0.8rem',
                          border: '1px solid #e2e8f0',
                          borderRadius: 10,
                          background: '#f8fafc',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>
                            {actor}
                            {entry.scannedBy?.role ? (
                              <span style={{ marginLeft: 6, color: '#64748b', fontSize: '0.76rem', fontWeight: 600 }}>
                                {formatStatusLabel(entry.scannedBy.role)}
                              </span>
                            ) : null}
                          </div>
                          <div style={{ color: '#334155', fontSize: '0.84rem' }}>
                            {entry.notes || formatStatusLabel(entry.status)}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                            {[formatStatusLabel(entry.status), entry.location].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div style={{ color: '#475569', fontSize: '0.75rem', textAlign: 'right' }}>
                          {formatDateTime(entry.scannedAt)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <TinyButton onClick={onClose}>{t('central.close')}</TinyButton>
          {!opened ? (
            <PrimaryAction onClick={onUnpack} disabled={unpacking || loading}>
              <PackageOpen size={15} />
              {unpacking ? t('central.opening') : t('central.openPackage')}
            </PrimaryAction>
          ) : (
            /* Re-opening an already-open package is a repeat action, so it stays
               visually distinct from the ink "Open package" — but on the accent
               token rather than the hardcoded sky-blue gradient it used to carry,
               which was the last off-palette CTA left in the package UI. */
            <PrimaryAction
              onClick={onUnpack}
              disabled={unpacking || loading}
              style={{ background: 'var(--accent, #5b8def)', borderColor: 'var(--accent, #5b8def)' }}
            >
              <RefreshCw size={15} />
              {unpacking ? t('central.refreshing') : t('central.refreshPackage')}
            </PrimaryAction>
          )}
        </ModalFooter>
      </ModalShell>
    </ModalBackdrop>
  );
};

